// src/client.ts
import axios from "axios";

// src/constants.ts
var DEFAULT_RETRY_CONFIG = {
  retries: 3,
  retryDelay: 1e3,
  retryCondition: [408, 500, 502, 503, 504],
  exponentialBackoff: true
};
var DEFAULT_TIMEOUT = 3e4;
var HEADERS = {
  CONTENT_TYPE: "Content-Type",
  ACCEPT: "Accept",
  AUTHORIZATION: "Authorization",
  ACCEPT_LANGUAGE: "Accept-Language",
  CONTENT_LANGUAGE: "Content-Language",
  IDEMPOTENCY_KEY: "X-Idempotency-Key",
  /** Token "thiết bị tin cậy" của đăng nhập web quản trị — docs/ADMIN_2FA_EMAIL.md §4. */
  DEVICE_TOKEN: "X-Device-Token"
};
var CONTENT_TYPES = {
  JSON: "application/json",
  FORM_DATA: "multipart/form-data",
  FORM_URLENCODED: "application/x-www-form-urlencoded"
};
var HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504
};
var API_ERROR_CODES = {
  AUTHENTICATION_REQUIRED: "AUTHENTICATION_REQUIRED",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  PERMISSION_DENIED: "PERMISSION_DENIED",
  RESOURCE_NOT_FOUND: "RESOURCE_NOT_FOUND",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  DUPLICATE_ENTRY: "DUPLICATE_ENTRY",
  INSUFFICIENT_STOCK: "INSUFFICIENT_STOCK",
  INVALID_STATUS_TRANSITION: "INVALID_STATUS_TRANSITION",
  PROMOTION_EXPIRED: "PROMOTION_EXPIRED",
  PROMOTION_USAGE_LIMIT: "PROMOTION_USAGE_LIMIT",
  INVALID_OTP: "INVALID_OTP",
  RATE_LIMITED: "RATE_LIMITED"
};
var API_ENDPOINTS = {
  // Auth
  AUTH_LOGIN: "/auth/login/",
  AUTH_GOOGLE: "/auth/google/",
  AUTH_GOOGLE_CALLBACK: "/auth/google/callback/",
  AUTH_REFRESH: "/auth/refresh/",
  AUTH_LOGOUT: "/auth/logout/",
  AUTH_OTP_REQUEST: "/auth/otp/request/",
  AUTH_OTP_VERIFY: "/auth/otp/verify/",
  AUTH_CUSTOMER_REGISTER: "/auth/customer/register/",
  AUTH_CUSTOMER_VERIFY_ACCOUNT: "/auth/customer/verify-account/",
  AUTH_CUSTOMER_RESEND_OTP: "/auth/customer/resend-otp/",
  AUTH_CUSTOMER_FORGOT_PASSWORD: "/auth/customer/forgot-password/",
  AUTH_CUSTOMER_VERIFY_RESET_OTP: "/auth/customer/verify-reset-otp/",
  AUTH_CUSTOMER_RESET_PASSWORD: "/auth/customer/reset-password/",
  AUTH_CUSTOMER_CHANGE_PASSWORD: "/auth/customer/change-password/",
  AUTH_CUSTOMER_DELETE_ACCOUNT: "/auth/customer/delete-account/",
  AUTH_CUSTOMER_PROFILE: "/auth/customer/profile/",
  AUTH_CUSTOMER_SESSIONS: "/auth/customer/sessions/",
  // Đăng nhập web quản trị + 2FA qua EMAIL (sheet 2 #3, chốt 08/09/2026).
  // Thay hẳn bản TOTP cũ — BE đã gỡ /auth/2fa/*. `/auth/login/` vẫn sống
  // nhưng chỉ dành cho app khách; web quản trị dùng bộ ba dưới đây.
  AUTH_ADMIN_LOGIN: "/auth/admin/login/",
  AUTH_ADMIN_LOGIN_VERIFY: "/auth/admin/login/verify/",
  AUTH_ADMIN_LOGIN_RESEND: "/auth/admin/login/resend/",
  // Users
  USERS: "/users/",
  USERS_ME: "/users/me/",
  // Stores
  STORES: "/stores/",
  APP_FAVORITES_STORES: "/app/favorites/stores/",
  // Customers
  CUSTOMERS: "/customers/",
  // Categories
  CATEGORIES: "/categories/",
  // Products
  PRODUCTS: "/products/",
  /** Import CSV/XLSX (sheet 2 #13/#21) — multipart, upsert theo sku. */
  PRODUCTS_IMPORT: "/products/import/",
  APP_FAVORITES_PRODUCTS: "/app/favorites/products/",
  // Inventory
  INVENTORY: "/inventory/",
  INVENTORY_SELLABLE_PRODUCTS: "/inventory/sellable-products/",
  INVENTORY_ADJUST: "/inventory/adjust/",
  INVENTORY_BULK_ADJUST: "/inventory/bulk-adjust/",
  INVENTORY_ADJUSTMENTS: "/inventory/adjustments/",
  INVENTORY_DRIFT: "/inventory/drift/",
  /** Thống kê hao hụt theo nguyên nhân — mục #220, BE chốt 09/09/2026. */
  INVENTORY_LOSS_STATS: "/inventory/loss-stats/",
  /**
   * Lô & hạn sử dụng (sheet 2 #32). Batch là LỚP PHỦ trên tồn kho, không thay
   * thế nó: `InventoryStock` vẫn là nguồn sự thật duy nhất về số lượng, batch
   * chỉ ghi số đó chia theo lô thế nào. Batch KHÔNG BAO GIỜ chặn giao dịch.
   */
  INVENTORY_BATCHES: "/inventory/batches/",
  INVENTORY_BATCHES_EXPIRING: "/inventory/batches/expiring/",
  /** Phiên kiểm kê định kỳ (sheet 2 #30). */
  INVENTORY_STOCKTAKES: "/inventory/stocktakes/",
  INVENTORY_LOW_STOCK: "/inventory/low-stock/",
  // Stock Imports
  STOCK_IMPORTS: "/stock-imports/",
  // Orders
  ORDERS: "/orders/",
  ORDERS_VERIFY: "/orders/verify/",
  // Transactions
  TRANSACTIONS: "/transactions/",
  // Payments
  PAYMENTS: "/payments/",
  // Thẻ đã lưu (market PL) — hệ thống tự trừ ở cổng ra, khác PAYMENTS/methods/
  // vốn là danh sách để khách CHỌN trong luồng UA.
  PAYMENT_METHODS: "/customers/me/payment-methods/",
  // Lượt vào cửa hàng (market PL) — giữ tạm tiền rồi mới cấp QR.
  // Khác ACCESS_TOKENS / QR_GENERATE của luồng UA: đó là namespace /access/.
  ENTRY_INTENTS: "/store-access/entry-intents/",
  // Khiếu nại sau bán. Hàng đợi xử lý của store-admin nằm ở gốc /disputes/,
  // còn khách tạo và xem qua /sessions/{id}/disputes/.
  DISPUTES: "/disputes/",
  // Receipts
  RECEIPTS: "/receipts/",
  // Shopping sessions (hoá đơn phiên mua sắm)
  SESSIONS: "/sessions/",
  // Returns & refunds
  RETURNS: "/returns/",
  // Store feedback & product issue reports
  FEEDBACK: "/feedback/",
  // Uploads (ảnh đính kèm feedback...) — multipart, 1 ảnh/request
  UPLOADS_IMAGES: "/uploads/images/",
  // Loyalty
  LOYALTY_ACCOUNTS: "/loyalty/accounts/",
  /** Ngưỡng hạng theo currency, áp TOÀN CHUỖI (PM chốt 04/09). */
  LOYALTY_TIER_CONFIG: "/loyalty/tier-config/",
  /**
   * Đổi ngưỡng CHỈ áp cho lần tính sau — khách hiện hữu giữ hạng cũ tới khi
   * gọi endpoint này. Màn cấu hình phải để nút này ngay cạnh form.
   */
  LOYALTY_RECALCULATE_TIERS: "/loyalty/recalculate-tiers/",
  /** Lịch sử điểm của khách đang đăng nhập — BE chốt 06/08/2026. */
  LOYALTY_HISTORY: "/loyalty/history/",
  // Access Control
  ACCESS_TOKENS: "/access/tokens/",
  ACCESS_EVENTS: "/access/events/",
  ACCESS_SESSIONS: "/access/sessions/",
  // Nedap AtWork (door access control)
  ATWORK_DOORS: "/atwork/doors/",
  ATWORK_ACCESS_STATS_DAILY: "/atwork/access-stats/daily/",
  ATWORK_ACCESS_DENIED_RECENT: "/atwork/access-denied/recent/",
  ATWORK_DOOR_COMMANDS: "/atwork/door-commands/",
  ATWORK_HEALTH: "/atwork/health/",
  // Promotions
  PROMOTIONS: "/promotions/",
  PROMOTIONS_VALIDATE: "/promotions/validate/",
  PROMOTIONS_ACTIVE: "/promotions/active/",
  PROMOTIONS_ME: "/promotions/me/",
  PROMOTIONS_MY_VOUCHERS: "/promotions/my-vouchers/",
  // Reports
  REPORTS_DAILY_SALES: "/reports/daily-sales/",
  /**
   * Chốt cuối ngày (sheet 2 #53). Gom theo TIMEZONE CỦA CỬA HÀNG — khác
   * `/reports/daily-sales/` vốn gom theo giờ server, nên hai báo cáo có thể
   * lệch nhau ở đơn sát nửa đêm. Đó là hành vi đúng, không phải bug.
   */
  REPORTS_DAILY_CLOSE: "/reports/daily-close/",
  REPORTS_INVENTORY: "/inventory",
  REPORTS_ORDERS: "/orders",
  REPORTS_DASHBOARD: "/reports/dashboard/",
  // Suppliers
  SUPPLIERS: "/suppliers/",
  // Settings
  SETTINGS: "/settings/",
  // Audit Logs
  AUDIT_LOGS: "/audit-logs/",
  // QR
  QR_GENERATE: "/qr/generate/",
  // Notifications
  APP_NOTIFICATIONS_ME: "/app/notifications/me/",
  APP_NOTIFICATIONS: "/app/notifications/",
  // Push devices (FCM)
  APP_DEVICES_REGISTER: "/app/devices/register/",
  APP_DEVICES_UNREGISTER: "/app/devices/unregister/"
};

// src/redact.ts
var SENSITIVE_KEYS = /* @__PURE__ */ new Set([
  "mrz",
  "password",
  "new_password",
  "old_password",
  "current_password",
  "access_token",
  "refresh_token",
  "token",
  "otp",
  "pesel",
  "card_number",
  "cvv"
]);
var REDACTED = "[redacted]";
var MAX_DEPTH = 6;
function redactSensitive(value, depth = 0) {
  if (value == null) return value;
  if (depth > MAX_DEPTH) return value;
  if (Array.isArray(value)) {
    return value.map((item) => redactSensitive(item, depth + 1));
  }
  if (typeof value !== "object" || Object.getPrototypeOf(value) !== Object.prototype) {
    return value;
  }
  const out = {};
  for (const [key, item] of Object.entries(value)) {
    out[key] = SENSITIVE_KEYS.has(key.toLowerCase()) ? REDACTED : redactSensitive(item, depth + 1);
  }
  return out;
}

// src/errors.ts
var DRF_VALIDATION_MESSAGE = "validation error";
function flattenErrors(errors) {
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) return [];
  return Object.entries(errors).flatMap(([field, value]) => {
    if (Array.isArray(value)) {
      return value.filter((entry) => typeof entry === "string").map((message) => ({ field, message }));
    }
    if (typeof value === "string") {
      return [{ field, message: value }];
    }
    return [];
  });
}
function parseErrorDetails(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const item = entry;
    const message = typeof item.message === "string" ? item.message.trim() : "";
    const code = typeof item.code === "string" ? item.code.trim() : "";
    if (!message && !code) return [];
    const field = typeof item.field === "string" && item.field.trim() ? item.field.trim() : null;
    return [{ field, code, message }];
  });
}
function parseAxiosError(error) {
  const isNetworkError = !error.response && error.code === "ERR_NETWORK";
  const isTimeout = error.code === "ECONNABORTED" || error.code === "ETIMEDOUT";
  const isCancelled = error.code === "ERR_CANCELED";
  const status = error.response?.status;
  const isAuthError = status === HTTP_STATUS.UNAUTHORIZED || status === HTTP_STATUS.FORBIDDEN;
  const body = error.response?.data;
  const backendMessage = typeof body?.message === "string" && body.message.trim() ? body.message.trim() : void 0;
  const details = flattenErrors(body?.errors);
  const errorDetails = parseErrorDetails(body?.error_details);
  const detailMessage = errorDetails.find((entry) => entry.message)?.message ?? details[0]?.message;
  const code = typeof body?.error_code === "string" && body.error_code.trim() ? body.error_code.trim() : void 0;
  let message = "An unexpected error occurred";
  if (isCancelled) {
    message = "Request was cancelled";
  } else if (isTimeout) {
    message = "Request timed out. Please try again.";
  } else if (isNetworkError) {
    message = "Network error. Please check your connection.";
  } else if (backendMessage?.toLowerCase() === DRF_VALIDATION_MESSAGE && detailMessage) {
    message = detailMessage;
  } else if (backendMessage) {
    message = backendMessage;
  } else if (detailMessage) {
    message = detailMessage;
  } else if (error.message) {
    message = error.message;
  }
  return {
    message,
    status,
    code,
    details: details.length ? details : void 0,
    errorDetails: errorDetails.length ? errorDetails : void 0,
    data: error.response?.data,
    originalError: error,
    isNetworkError,
    isTimeout,
    isAuthError,
    isCancelled,
    url: error.config?.url,
    method: error.config?.method?.toUpperCase()
  };
}
function createApiClientError(message, options) {
  return {
    message,
    isNetworkError: false,
    isTimeout: false,
    isAuthError: false,
    isCancelled: false,
    ...options
  };
}
function isApiClientError(error) {
  return typeof error === "object" && error !== null && "message" in error && "isNetworkError" in error && "isAuthError" in error;
}
var isApiError = isApiClientError;
function isRetryableError(error, retryCondition) {
  if (error.code === "ERR_CANCELED") {
    return false;
  }
  if (!error.response) {
    return true;
  }
  return retryCondition.includes(error.response.status);
}
function calculateBackoffDelay(attempt, baseDelay, useExponential) {
  if (!useExponential) {
    return baseDelay;
  }
  const exponentialDelay = baseDelay * Math.pow(2, attempt - 1);
  const jitter = Math.random() * 0.3 * exponentialDelay;
  return Math.min(exponentialDelay + jitter, 3e4);
}
function getStatusErrorMessage(status) {
  switch (status) {
    case HTTP_STATUS.BAD_REQUEST:
      return "Invalid request. Please check your input.";
    case HTTP_STATUS.UNAUTHORIZED:
      return "Authentication required. Please log in.";
    case HTTP_STATUS.FORBIDDEN:
      return "You do not have permission to perform this action.";
    case HTTP_STATUS.NOT_FOUND:
      return "The requested resource was not found.";
    case HTTP_STATUS.CONFLICT:
      return "A conflict occurred. The resource may already exist.";
    case HTTP_STATUS.UNPROCESSABLE_ENTITY:
      return "Validation failed. Please check your input.";
    case HTTP_STATUS.TOO_MANY_REQUESTS:
      return "Too many requests. Please try again later.";
    case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      return "Server error. Please try again later.";
    case HTTP_STATUS.BAD_GATEWAY:
    case HTTP_STATUS.SERVICE_UNAVAILABLE:
    case HTTP_STATUS.GATEWAY_TIMEOUT:
      return "Service temporarily unavailable. Please try again later.";
    default:
      return "An unexpected error occurred.";
  }
}

// src/client.ts
var NO_REFRESH_TOKEN_MESSAGE = "No refresh token available";
var ApiClient = class {
  axiosInstance;
  tokenStorage;
  config;
  retryConfig;
  isRefreshing = false;
  refreshSubscribers = [];
  normalizeLanguageCode(code) {
    if (code === "pl" || code === "vi" || code === "en" || code === "uk") {
      return code;
    }
    return null;
  }
  resolveRequestLanguage(config) {
    const directHeader = config.headers.get(HEADERS.ACCEPT_LANGUAGE);
    const normalizedDirectHeader = this.normalizeLanguageCode(
      typeof directHeader === "string" ? directHeader : null
    );
    if (normalizedDirectHeader) {
      return normalizedDirectHeader;
    }
    const configLanguage = this.config.getLanguage?.();
    return this.normalizeLanguageCode(configLanguage ?? null);
  }
  normalizeResponsePayload(payload) {
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      return payload;
    }
    const envelope = payload;
    const isEnvelope = Object.prototype.hasOwnProperty.call(envelope, "data") || Object.prototype.hasOwnProperty.call(envelope, "code") || Object.prototype.hasOwnProperty.call(envelope, "message");
    if (!isEnvelope) {
      return payload;
    }
    if (envelope.meta && Array.isArray(envelope.data)) {
      return {
        count: envelope.meta.count ?? envelope.data.length,
        next: envelope.meta.next ?? null,
        previous: envelope.meta.previous ?? null,
        results: envelope.data
      };
    }
    return envelope.data ?? null;
  }
  /**
   * Refresh thất bại vì PHIÊN không còn hiệu lực, hay chỉ vì đường truyền?
   *
   * Chỉ trường hợp đầu mới được xoá token + gọi `onAuthError`. Mất sóng,
   * timeout, 5xx đều là lỗi TẠM: xoá token lúc đó là tự đăng xuất người đang
   * dùng app bình thường trong khi refresh token vẫn còn hạn — chính là triệu
   * chứng "app tự logout sau một lúc" (bug #106). Giữ token lại thì lần gọi
   * kế tiếp tự khôi phục.
   */
  isSessionRejected(error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (status == null) return false;
      return status >= 400 && status < 500;
    }
    return error instanceof Error && error.message === NO_REFRESH_TOKEN_MESSAGE;
  }
  /**
   * `fallbackRefreshToken`: nhiều backend KHÔNG xoay refresh token ở mỗi lần
   * refresh (chỉ trả `access_token`). Bắt buộc phải có cả hai ở đây là tự biến
   * một lần refresh thành công thành lỗi -> xoá token -> đăng xuất (bug #106).
   * Thiếu refresh token mới thì dùng lại cái đang cầm.
   */
  extractTokenPair(payload, fallbackRefreshToken) {
    const envelope = payload;
    const base = envelope && typeof envelope === "object" && envelope.data && typeof envelope.data === "object" ? envelope.data : payload;
    const data = base;
    const accessToken = data.access_token ?? data.accessToken ?? data.tokens?.access_token ?? data.tokens?.accessToken ?? data.token;
    const refreshToken = data.refresh_token ?? data.refreshToken ?? data.tokens?.refresh_token ?? data.tokens?.refreshToken ?? data.refresh ?? fallbackRefreshToken ?? void 0;
    if (!accessToken || !refreshToken) {
      throw new Error("Refresh response is missing auth tokens");
    }
    return { accessToken, refreshToken };
  }
  constructor(config) {
    this.tokenStorage = config.tokenStorage;
    this.config = {
      timeout: DEFAULT_TIMEOUT,
      debug: false,
      ...config
    };
    this.retryConfig = {
      retries: config.retry?.retries ?? DEFAULT_RETRY_CONFIG.retries,
      retryDelay: config.retry?.retryDelay ?? DEFAULT_RETRY_CONFIG.retryDelay,
      retryCondition: config.retry?.retryCondition ?? [...DEFAULT_RETRY_CONFIG.retryCondition],
      exponentialBackoff: config.retry?.exponentialBackoff ?? DEFAULT_RETRY_CONFIG.exponentialBackoff
    };
    this.axiosInstance = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        [HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
        [HEADERS.ACCEPT]: CONTENT_TYPES.JSON,
        ...this.config.headers
      }
    });
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }
  /**
   * Setup request interceptor for auth token injection
   */
  setupRequestInterceptor() {
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        if (config._skipAuth) {
          return config;
        }
        const accessToken = await this.tokenStorage.getAccessToken();
        if (accessToken) {
          config.headers.set(HEADERS.AUTHORIZATION, `Bearer ${accessToken}`);
        }
        const language = this.resolveRequestLanguage(config);
        if (language) {
          config.headers.set(HEADERS.ACCEPT_LANGUAGE, language);
        }
        if (this.config.onRequest) {
          return this.config.onRequest(config);
        }
        if (this.config.debug) {
          console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`, {
            params: redactSensitive(config.params),
            data: redactSensitive(config.data)
          });
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }
  /**
   * Setup response interceptor for error handling and token refresh
   */
  setupResponseInterceptor() {
    this.axiosInstance.interceptors.response.use(
      (response) => {
        if (this.config.debug) {
          console.log(`[API] Response ${response.status}`, redactSensitive(response.data));
        }
        response.data = this.normalizeResponsePayload(response.data);
        if (this.config.onResponse) {
          response.data = this.config.onResponse(response.data);
        }
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        if (!originalRequest) {
          return Promise.reject(this.handleError(error));
        }
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED && !originalRequest._retry && !originalRequest._skipAuth) {
          return this.handleUnauthorized(error, originalRequest);
        }
        if (!originalRequest._skipRetry && isRetryableError(error, this.retryConfig.retryCondition)) {
          return this.handleRetry(error, originalRequest);
        }
        return Promise.reject(this.handleError(error));
      }
    );
  }
  /**
   * Chủ động làm mới access token.
   *
   * Interceptor 401 chỉ chạy khi có request HTTP thất bại. WebSocket bắt tay
   * ngoài axios nên khi gateway đóng với mã 4001 (token sai/hết hạn) không có
   * đường nào kích hoạt refresh — phải gọi tay qua đây.
   *
   * Trả về access token mới, hoặc null nếu refresh thất bại (lúc đó token đã
   * được xoá và `onAuthError` đã chạy).
   */
  async refreshAccessToken() {
    if (this.isRefreshing) {
      return new Promise((resolve) => {
        this.refreshSubscribers.push((error, token) => {
          resolve(error ? null : token ?? null);
        });
      });
    }
    this.isRefreshing = true;
    try {
      const currentRefreshToken = await this.tokenStorage.getRefreshToken();
      if (!currentRefreshToken) throw new Error(NO_REFRESH_TOKEN_MESSAGE);
      const refreshLanguage = this.normalizeLanguageCode(this.config.getLanguage?.() ?? null);
      const response = await axios.post(
        `${this.config.baseURL}${API_ENDPOINTS.AUTH_REFRESH}`,
        { refresh_token: currentRefreshToken },
        {
          timeout: this.config.timeout,
          headers: refreshLanguage ? { [HEADERS.ACCEPT_LANGUAGE]: refreshLanguage } : void 0
        }
      );
      const { accessToken, refreshToken } = this.extractTokenPair(
        response.data,
        currentRefreshToken
      );
      await this.tokenStorage.setAccessToken(accessToken);
      await this.tokenStorage.setRefreshToken(refreshToken);
      this.refreshSubscribers.forEach((callback) => callback(null, accessToken));
      this.refreshSubscribers = [];
      return accessToken;
    } catch (refreshError) {
      const sessionRejected = this.isSessionRejected(refreshError);
      if (sessionRejected) {
        await this.tokenStorage.clearTokens();
      }
      const errorToReject = refreshError instanceof Error ? refreshError : new Error("Refresh failed");
      this.refreshSubscribers.forEach((callback) => callback(errorToReject));
      this.refreshSubscribers = [];
      if (sessionRejected) {
        this.config.onAuthError?.();
      }
      return null;
    } finally {
      this.isRefreshing = false;
    }
  }
  /**
   * Handle 401 Unauthorized - attempt token refresh
   */
  async handleUnauthorized(error, originalRequest) {
    originalRequest._retry = true;
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.refreshSubscribers.push((error2, token) => {
          if (error2) {
            reject(error2);
          } else if (token) {
            const retryConfig = { ...originalRequest };
            if (retryConfig.signal && "aborted" in retryConfig.signal && retryConfig.signal.aborted) {
              delete retryConfig.signal;
            }
            retryConfig.headers.set(HEADERS.AUTHORIZATION, `Bearer ${token}`);
            resolve(this.axiosInstance(retryConfig));
          }
        });
      });
    }
    this.isRefreshing = true;
    try {
      const currentRefreshToken = await this.tokenStorage.getRefreshToken();
      if (!currentRefreshToken) {
        throw new Error(NO_REFRESH_TOKEN_MESSAGE);
      }
      const refreshLanguage = this.normalizeLanguageCode(this.config.getLanguage?.() ?? null);
      const response = await axios.post(
        `${this.config.baseURL}${API_ENDPOINTS.AUTH_REFRESH}`,
        { refresh_token: currentRefreshToken },
        {
          timeout: this.config.timeout,
          headers: {
            [HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
            ...refreshLanguage ? { [HEADERS.ACCEPT_LANGUAGE]: refreshLanguage } : {}
          }
        }
      );
      const { accessToken, refreshToken } = this.extractTokenPair(
        response.data,
        currentRefreshToken
      );
      await this.tokenStorage.setAccessToken(accessToken);
      await this.tokenStorage.setRefreshToken(refreshToken);
      this.refreshSubscribers.forEach((callback) => callback(null, accessToken));
      this.refreshSubscribers = [];
      const retryConfig = { ...originalRequest };
      if (retryConfig.signal && "aborted" in retryConfig.signal && retryConfig.signal.aborted) {
        delete retryConfig.signal;
      }
      retryConfig.headers.set(HEADERS.AUTHORIZATION, `Bearer ${accessToken}`);
      return this.axiosInstance(retryConfig);
    } catch (refreshError) {
      const sessionRejected = this.isSessionRejected(refreshError);
      if (sessionRejected) {
        await this.tokenStorage.clearTokens();
      }
      const errorToReject = refreshError instanceof Error ? refreshError : new Error("Refresh failed");
      this.refreshSubscribers.forEach((callback) => callback(errorToReject));
      this.refreshSubscribers = [];
      if (sessionRejected && this.config.onAuthError) {
        this.config.onAuthError();
      }
      return Promise.reject(this.handleError(error));
    } finally {
      this.isRefreshing = false;
    }
  }
  /**
   * Handle retry logic with exponential backoff
   */
  async handleRetry(error, originalRequest) {
    const retryCount = (originalRequest._retryCount || 0) + 1;
    if (retryCount > this.retryConfig.retries) {
      return Promise.reject(this.handleError(error));
    }
    originalRequest._retryCount = retryCount;
    const delay = calculateBackoffDelay(
      retryCount,
      this.retryConfig.retryDelay,
      this.retryConfig.exponentialBackoff
    );
    if (this.config.debug) {
      console.log(`[API] Retry ${retryCount}/${this.retryConfig.retries} after ${delay}ms`);
    }
    await new Promise((resolve) => setTimeout(resolve, delay));
    const retryConfig = { ...originalRequest };
    if (retryConfig.signal && "aborted" in retryConfig.signal && retryConfig.signal.aborted) {
      delete retryConfig.signal;
    }
    return this.axiosInstance(retryConfig);
  }
  /**
   * Handle and transform errors
   */
  handleError(error) {
    const apiError = parseAxiosError(error);
    if (this.config.onError) {
      this.config.onError(apiError);
    }
    if (this.config.debug) {
      console.error("[API] Error:", apiError);
    }
    return apiError;
  }
  /**
   * Make a GET request
   */
  async get(url, params, options) {
    const config = this.buildRequestConfig(options);
    const response = await this.axiosInstance.get(url, {
      ...config,
      params: this.cleanParams(params)
    });
    return response.data;
  }
  /**
   * Make a POST request
   */
  async post(url, data, options) {
    const config = this.buildRequestConfig(options);
    const response = await this.axiosInstance.post(url, data, config);
    return response.data;
  }
  /**
   * Make a PUT request
   */
  async put(url, data, options) {
    const config = this.buildRequestConfig(options);
    const response = await this.axiosInstance.put(url, data, config);
    return response.data;
  }
  /**
   * Make a PATCH request
   */
  async patch(url, data, options) {
    const config = this.buildRequestConfig(options);
    const response = await this.axiosInstance.patch(url, data, config);
    return response.data;
  }
  /**
   * Make a DELETE request
   */
  async delete(url, options) {
    const config = this.buildRequestConfig(options);
    const response = await this.axiosInstance.delete(url, config);
    return response.data;
  }
  /**
   * Build request config from options
   */
  buildRequestConfig(options) {
    const config = {};
    if (options?.timeout) {
      config.timeout = options.timeout;
    }
    if (options?.signal) {
      config.signal = options.signal;
    }
    if (options?.headers) {
      config.headers = options.headers;
    }
    if (options?.idempotencyKey) {
      config.headers = {
        ...config.headers,
        [HEADERS.IDEMPOTENCY_KEY]: options.idempotencyKey
      };
    }
    if (options?.language) {
      config.headers = {
        ...config.headers,
        [HEADERS.ACCEPT_LANGUAGE]: options.language
      };
    }
    if (options?.skipAuth) {
      config._skipAuth = true;
    }
    if (options?.skipRetry) {
      config._skipRetry = true;
    }
    if (options?.responseType) {
      config.responseType = options.responseType;
    }
    return config;
  }
  /**
   * Clean undefined values from params
   */
  cleanParams(params) {
    if (!params) return void 0;
    const cleaned = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== void 0) {
        cleaned[key] = value;
      }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : void 0;
  }
  /**
   * Get the underlying Axios instance (for advanced use cases)
   */
  getAxiosInstance() {
    return this.axiosInstance;
  }
  /**
   * Get the token storage instance
   */
  getTokenStorage() {
    return this.tokenStorage;
  }
  /**
   * Check if the user is authenticated (has tokens)
   */
  async isAuthenticated() {
    return this.tokenStorage.hasTokens();
  }
  /**
   * Clear authentication tokens
   */
  async clearAuth() {
    await this.tokenStorage.clearTokens();
  }
};
function createApiClient(config) {
  return new ApiClient(config);
}

// src/storage.ts
var MemoryTokenStorage = class {
  accessToken = null;
  refreshToken = null;
  async getAccessToken() {
    return this.accessToken;
  }
  async setAccessToken(token) {
    this.accessToken = token;
  }
  async getRefreshToken() {
    return this.refreshToken;
  }
  async setRefreshToken(token) {
    this.refreshToken = token;
  }
  async clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
  }
  async hasTokens() {
    return this.accessToken !== null;
  }
};
var LocalStorageTokenStorage = class {
  accessTokenKey;
  refreshTokenKey;
  constructor(prefix = "conai") {
    this.accessTokenKey = `${prefix}_access_token`;
    this.refreshTokenKey = `${prefix}_refresh_token`;
  }
  async getAccessToken() {
    if (typeof window === "undefined") return null;
    const token = localStorage.getItem(this.accessTokenKey);
    if (!token || token === "undefined" || token === "null") {
      return null;
    }
    return token;
  }
  async setAccessToken(token) {
    if (typeof window === "undefined") return;
    localStorage.setItem(this.accessTokenKey, token);
  }
  async getRefreshToken() {
    if (typeof window === "undefined") return null;
    const token = localStorage.getItem(this.refreshTokenKey);
    if (!token || token === "undefined" || token === "null") {
      return null;
    }
    return token;
  }
  async setRefreshToken(token) {
    if (typeof window === "undefined") return;
    localStorage.setItem(this.refreshTokenKey, token);
  }
  async clearTokens() {
    if (typeof window === "undefined") return;
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
  }
  async hasTokens() {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(this.accessTokenKey) !== null;
  }
};
var SessionStorageTokenStorage = class {
  accessTokenKey;
  refreshTokenKey;
  constructor(prefix = "conai") {
    this.accessTokenKey = `${prefix}_access_token`;
    this.refreshTokenKey = `${prefix}_refresh_token`;
  }
  async getAccessToken() {
    if (typeof window === "undefined") return null;
    return sessionStorage.getItem(this.accessTokenKey);
  }
  async setAccessToken(token) {
    if (typeof window === "undefined") return;
    sessionStorage.setItem(this.accessTokenKey, token);
  }
  async getRefreshToken() {
    if (typeof window === "undefined") return null;
    return sessionStorage.getItem(this.refreshTokenKey);
  }
  async setRefreshToken(token) {
    if (typeof window === "undefined") return;
    sessionStorage.setItem(this.refreshTokenKey, token);
  }
  async clearTokens() {
    if (typeof window === "undefined") return;
    sessionStorage.removeItem(this.accessTokenKey);
    sessionStorage.removeItem(this.refreshTokenKey);
  }
  async hasTokens() {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(this.accessTokenKey) !== null;
  }
};
var SecureTokenStorage = class {
  adapter;
  accessTokenKey;
  refreshTokenKey;
  constructor(adapter, prefix = "conai") {
    this.adapter = adapter;
    this.accessTokenKey = `${prefix}_access_token`;
    this.refreshTokenKey = `${prefix}_refresh_token`;
  }
  async getAccessToken() {
    return this.adapter.getItem(this.accessTokenKey);
  }
  async setAccessToken(token) {
    await this.adapter.setItem(this.accessTokenKey, token);
  }
  async getRefreshToken() {
    return this.adapter.getItem(this.refreshTokenKey);
  }
  async setRefreshToken(token) {
    await this.adapter.setItem(this.refreshTokenKey, token);
  }
  async clearTokens() {
    await Promise.all([
      this.adapter.removeItem(this.accessTokenKey),
      this.adapter.removeItem(this.refreshTokenKey)
    ]);
  }
  async hasTokens() {
    const token = await this.adapter.getItem(this.accessTokenKey);
    return token !== null;
  }
};
var PersistentTokenStorage = class {
  local;
  session;
  rememberMeKey;
  constructor(prefix = "conai") {
    this.local = new LocalStorageTokenStorage(prefix);
    this.session = new SessionStorageTokenStorage(prefix);
    this.rememberMeKey = `${prefix}_remember_me`;
  }
  isRememberMe() {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(this.rememberMeKey) === "true";
  }
  setRememberMe(value) {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(this.rememberMeKey, String(value));
  }
  getActiveStorage() {
    return this.isRememberMe() ? this.local : this.session;
  }
  async getAccessToken() {
    const localToken = await this.local.getAccessToken();
    const sessionToken = await this.session.getAccessToken();
    return this.isRememberMe() ? localToken ?? sessionToken : sessionToken ?? localToken;
  }
  async setAccessToken(token) {
    await this.getActiveStorage().setAccessToken(token);
  }
  async getRefreshToken() {
    const localToken = await this.local.getRefreshToken();
    const sessionToken = await this.session.getRefreshToken();
    return this.isRememberMe() ? localToken ?? sessionToken : sessionToken ?? localToken;
  }
  async setRefreshToken(token) {
    await this.getActiveStorage().setRefreshToken(token);
  }
  async clearTokens() {
    await this.local.clearTokens();
    await this.session.clearTokens();
  }
  async hasTokens() {
    return await this.local.hasTokens() || await this.session.hasTokens();
  }
};
function createTokenStorage(type = "local", prefix = "conai") {
  switch (type) {
    case "memory":
      return new MemoryTokenStorage();
    case "session":
      return new SessionStorageTokenStorage(prefix);
    case "local":
    default:
      return new LocalStorageTokenStorage(prefix);
  }
}

// src/services/auth.service.ts
var AuthService = class {
  constructor(client) {
    this.client = client;
  }
  unwrapPayload(raw) {
    const envelope = raw;
    if (envelope && typeof envelope === "object" && envelope.data && typeof envelope.data === "object") {
      return envelope.data;
    }
    return raw;
  }
  extractTokens(payload) {
    const unwrapped = this.unwrapPayload(payload);
    const data = unwrapped;
    const accessToken = data.access_token ?? data.accessToken ?? data.tokens?.access_token ?? data.tokens?.accessToken ?? data.token;
    const refreshToken = data.refresh_token ?? data.refreshToken ?? data.tokens?.refresh_token ?? data.tokens?.refreshToken ?? data.refresh;
    if (!accessToken || !refreshToken) {
      throw new Error("Login response is missing auth tokens");
    }
    return { accessToken, refreshToken };
  }
  normalizeUser(user) {
    const raw = user;
    const scopedStores = (raw.store_scopes ?? []).map((scope) => {
      const id = scope.store_id;
      const code = scope.store_code;
      const name = scope.store_name;
      if (!id || !code || !name) {
        return null;
      }
      return { id, code, name };
    }).filter((store) => Boolean(store));
    return {
      id: raw.id,
      email: raw.email,
      phone: raw.phone ?? null,
      role: raw.role,
      full_name: raw.full_name ?? raw.fullName ?? "Unknown User",
      is_active: raw.is_active ?? raw.isActive ?? true,
      is_staff: raw.is_staff ?? raw.isStaff ?? false,
      created_at: raw.created_at ?? raw.createdAt ?? (/* @__PURE__ */ new Date()).toISOString(),
      updated_at: raw.updated_at ?? raw.updatedAt ?? (/* @__PURE__ */ new Date()).toISOString(),
      stores: raw.stores ?? scopedStores
    };
  }
  /**
   * Login with email and password
   * Used by: StoreAdminApp, POSApp (staff login)
   */
  async login(credentials) {
    const rawResponse = await this.client.post(
      API_ENDPOINTS.AUTH_LOGIN,
      credentials,
      { skipAuth: true }
    );
    const challenge = this.unwrapPayload(rawResponse);
    if (challenge?.two_factor_required === true && challenge.pending_token) {
      return {
        two_factor_required: true,
        pending_token: challenge.pending_token,
        expires_in: challenge.expires_in ?? 300
      };
    }
    const response = this.unwrapPayload(rawResponse);
    const tokenStorage = this.client.getTokenStorage();
    const { accessToken, refreshToken } = this.extractTokens(rawResponse);
    await tokenStorage.setAccessToken(accessToken);
    await tokenStorage.setRefreshToken(refreshToken);
    return {
      access_token: accessToken,
      refresh_token: refreshToken,
      user: this.normalizeUser(response.user)
    };
  }
  /**
   * Đăng nhập web quản trị — `POST /auth/admin/login/` (docs/ADMIN_2FA_EMAIL.md).
   *
   * `deviceToken` là token "thiết bị tin cậy" app tự lưu (localStorage) sau
   * lần verify có tích "không hỏi lại"; có thì gửi kèm header `X-Device-Token`
   * để BE bỏ qua bước mã. KHÔNG dựa vào cookie của BE — cookie cross-site chỉ
   * sống trên HTTPS, dev chạy HTTP là mất.
   *
   * Trả về một trong hai: token pair (đã tự lưu), hoặc challenge đòi mã email
   * (HTTP vẫn 200 — phân nhánh bằng `two_factor_required`).
   */
  async adminLogin(credentials, options) {
    const headers = {};
    if (options?.deviceToken) {
      headers[HEADERS.DEVICE_TOKEN] = options.deviceToken;
    }
    const rawResponse = await this.client.post(
      API_ENDPOINTS.AUTH_ADMIN_LOGIN,
      credentials,
      { skipAuth: true, headers }
    );
    const challenge = this.unwrapPayload(rawResponse);
    if (challenge?.two_factor_required === true && challenge.pending_token) {
      return {
        two_factor_required: true,
        pending_token: challenge.pending_token,
        pending_expires_in: challenge.pending_expires_in ?? 600,
        code_expires_in: challenge.code_expires_in ?? 300,
        email_hint: challenge.email_hint ?? ""
      };
    }
    const response = this.unwrapPayload(rawResponse);
    const tokenStorage = this.client.getTokenStorage();
    const { accessToken, refreshToken } = this.extractTokens(rawResponse);
    await tokenStorage.setAccessToken(accessToken);
    await tokenStorage.setRefreshToken(refreshToken);
    return {
      access_token: accessToken,
      refresh_token: refreshToken,
      user: this.normalizeUser(response.user)
    };
  }
  /**
   * Bước hai — đổi `pending_token` + mã email lấy token thật.
   * `POST /auth/admin/login/verify/`.
   *
   * `pending_token` dùng MỘT LẦN, xác minh xong là chết. `remember_device` là
   * giá trị ô tích "không hỏi lại trên thiết bị này" — tích thì response có
   * thêm `device_token`, phía gọi phải tự lưu và gửi lại qua `adminLogin`.
   *
   * 401 `ADMIN_LOGIN_CODE_INVALID` → ở lại màn nhập mã;
   * 401 `ADMIN_LOGIN_PENDING_INVALID` → quay về màn nhập mật khẩu.
   */
  async adminLoginVerify(payload) {
    const rawResponse = await this.client.post(
      API_ENDPOINTS.AUTH_ADMIN_LOGIN_VERIFY,
      payload,
      { skipAuth: true }
    );
    const response = this.unwrapPayload(rawResponse);
    const tokenStorage = this.client.getTokenStorage();
    const { accessToken, refreshToken } = this.extractTokens(rawResponse);
    await tokenStorage.setAccessToken(accessToken);
    await tokenStorage.setRefreshToken(refreshToken);
    return {
      access_token: accessToken,
      refresh_token: refreshToken,
      user: this.normalizeUser(response.user),
      device_token: response.device_token,
      device_token_expires_in_days: response.device_token_expires_in_days
    };
  }
  /**
   * Gửi lại mã — `POST /auth/admin/login/resend/`.
   *
   * ⚠️ Trả về `pending_token` MỚI và huỷ cái cũ ngay — phía gọi PHẢI ghi đè.
   * Ngân sách gửi mã 5 lần / 15 phút / tài khoản, dùng chung với bước 1; hết
   * thì 429.
   */
  async adminLoginResend(payload) {
    const rawResponse = await this.client.post(
      API_ENDPOINTS.AUTH_ADMIN_LOGIN_RESEND,
      payload,
      { skipAuth: true }
    );
    const data = this.unwrapPayload(rawResponse);
    if (!data?.pending_token) {
      throw new Error("Resend response is missing pending_token");
    }
    return {
      pending_token: data.pending_token,
      pending_expires_in: data.pending_expires_in ?? 600,
      code_expires_in: data.code_expires_in ?? 300,
      email_hint: data.email_hint ?? ""
    };
  }
  /**
   * Get Google Login URL
   */
  async getGoogleAuthUrl() {
    return this.client.get(API_ENDPOINTS.AUTH_GOOGLE, { skipAuth: true });
  }
  /**
   * Login with Google
   */
  async loginWithGoogle(payload) {
    const rawResponse = await this.client.post(
      API_ENDPOINTS.AUTH_GOOGLE_CALLBACK,
      payload,
      { skipAuth: true }
    );
    const response = this.unwrapPayload(rawResponse);
    const tokenStorage = this.client.getTokenStorage();
    const { accessToken, refreshToken } = this.extractTokens(rawResponse);
    await tokenStorage.setAccessToken(accessToken);
    await tokenStorage.setRefreshToken(refreshToken);
    return {
      access_token: accessToken,
      refresh_token: refreshToken,
      user: this.normalizeUser(response.user)
    };
  }
  /**
   * Request OTP for phone login
   * Used by: UserApp (customer login)
   */
  async requestOTP(payload) {
    return this.client.post(
      API_ENDPOINTS.AUTH_OTP_REQUEST,
      payload,
      { skipAuth: true }
    );
  }
  /**
   * Verify OTP and login
   * Used by: UserApp (customer login)
   */
  async verifyOTP(payload) {
    const rawResponse = await this.client.post(
      API_ENDPOINTS.AUTH_OTP_VERIFY,
      payload,
      { skipAuth: true }
    );
    const response = this.unwrapPayload(rawResponse);
    const tokenStorage = this.client.getTokenStorage();
    const { accessToken, refreshToken } = this.extractTokens(rawResponse);
    await tokenStorage.setAccessToken(accessToken);
    await tokenStorage.setRefreshToken(refreshToken);
    return {
      access_token: accessToken,
      refresh_token: refreshToken,
      user: this.normalizeUser(response.user)
    };
  }
  /**
   * Refresh access token
   * Usually handled automatically by the API client interceptor
   */
  async refreshToken(payload) {
    const rawResponse = await this.client.post(
      API_ENDPOINTS.AUTH_REFRESH,
      payload,
      { skipAuth: true }
    );
    const tokenStorage = this.client.getTokenStorage();
    const { accessToken, refreshToken } = this.extractTokens(rawResponse);
    await tokenStorage.setAccessToken(accessToken);
    await tokenStorage.setRefreshToken(refreshToken);
    return {
      access_token: accessToken,
      refresh_token: refreshToken
    };
  }
  /**
   * Logout and invalidate refresh token
   */
  async logout() {
    const tokenStorage = this.client.getTokenStorage();
    const refreshToken = await tokenStorage.getRefreshToken();
    try {
      const response = await this.client.post(
        API_ENDPOINTS.AUTH_LOGOUT,
        { refresh_token: refreshToken || "" }
      );
      return response;
    } finally {
      await tokenStorage.clearTokens();
    }
  }
  /**
   * Register a new customer account
   * POST /auth/customer/register/
   */
  async registerCustomer(data) {
    const rawResponse = await this.client.post(
      API_ENDPOINTS.AUTH_CUSTOMER_REGISTER,
      data,
      { skipAuth: true }
    );
    try {
      const tokenStorage = this.client.getTokenStorage();
      const { accessToken, refreshToken } = this.extractTokens(rawResponse);
      await tokenStorage.setAccessToken(accessToken);
      await tokenStorage.setRefreshToken(refreshToken);
    } catch {
    }
    return rawResponse;
  }
  /**
   * Verify customer account with OTP code sent during registration
   * POST /auth/customer/verify-account/
   */
  async verifyAccount(data) {
    const rawResponse = await this.client.post(
      API_ENDPOINTS.AUTH_CUSTOMER_VERIFY_ACCOUNT,
      data,
      { skipAuth: true }
    );
    try {
      const tokenStorage = this.client.getTokenStorage();
      const { accessToken, refreshToken } = this.extractTokens(rawResponse);
      await tokenStorage.setAccessToken(accessToken);
      await tokenStorage.setRefreshToken(refreshToken);
    } catch {
    }
    return rawResponse;
  }
  /**
   * Resend OTP code for email verification
   * POST /auth/customer/resend-otp/
   */
  async resendOTP(data) {
    return this.client.post(
      API_ENDPOINTS.AUTH_CUSTOMER_RESEND_OTP,
      data,
      { skipAuth: true }
    );
  }
  /**
   * Request password reset OTP
   * POST /auth/customer/forgot-password/
   */
  async forgotPassword(data) {
    return this.client.post(
      API_ENDPOINTS.AUTH_CUSTOMER_FORGOT_PASSWORD,
      data,
      { skipAuth: true }
    );
  }
  /**
   * Verify reset password OTP and get reset token
   * POST /auth/customer/verify-reset-otp/
   */
  async verifyResetOTP(data) {
    return this.client.post(
      API_ENDPOINTS.AUTH_CUSTOMER_VERIFY_RESET_OTP,
      data,
      { skipAuth: true }
    );
  }
  /**
   * Reset password using reset token
   * POST /auth/customer/reset-password/
   */
  async resetPassword(data) {
    const rawResponse = await this.client.post(
      API_ENDPOINTS.AUTH_CUSTOMER_RESET_PASSWORD,
      data,
      { skipAuth: true }
    );
    try {
      const tokenStorage = this.client.getTokenStorage();
      const { accessToken, refreshToken } = this.extractTokens(rawResponse);
      await tokenStorage.setAccessToken(accessToken);
      await tokenStorage.setRefreshToken(refreshToken);
    } catch {
    }
    return rawResponse;
  }
  /**
   * Change password for authenticated customer
   * POST /auth/customer/change-password/
   */
  async changePassword(data) {
    return this.client.post(
      API_ENDPOINTS.AUTH_CUSTOMER_CHANGE_PASSWORD,
      data
    );
  }
  /**
   * Delete customer account (requires password confirmation)
   * POST /auth/customer/delete-account/
   */
  async deleteAccount(data) {
    return this.client.post(
      API_ENDPOINTS.AUTH_CUSTOMER_DELETE_ACCOUNT,
      data
    );
  }
  /**
   * Update customer profile
   * PUT /auth/customer/profile/
   */
  async updateCustomerProfile(data) {
    return this.client.put(
      API_ENDPOINTS.AUTH_CUSTOMER_PROFILE,
      data
    );
  }
  /**
   * Get active sessions
   * GET /auth/customer/sessions/
   *
   * Schema BE chốt 29/08/2026 (8 key — xem CustomerSession). Endpoint trả
   * list envelope ({data: [...], meta}) nên client normalize thành
   * {results: [...]}; phòng cả bản BE cũ trả mảng trần.
   * `is_current` đọc từ claim `sid` của access token; session id GIỮ NGUYÊN
   * qua token rotation.
   */
  async getActiveSessions() {
    const response = await this.client.get(API_ENDPOINTS.AUTH_CUSTOMER_SESSIONS);
    if (Array.isArray(response)) return response;
    const results = response?.results;
    return Array.isArray(results) ? results : [];
  }
  /**
   * Revoke a session
   * DELETE /auth/customer/sessions/{session_id}/
   */
  async revokeSession(sessionId) {
    await this.client.delete(`${API_ENDPOINTS.AUTH_CUSTOMER_SESSIONS}${sessionId}/`);
  }
  /**
   * Revoke sessions hàng loạt — nguyên tử, thay cho vòng lặp xoá từng phiên.
   * DELETE /auth/customer/sessions/                     -> tất cả
   * DELETE /auth/customer/sessions/?keep_current=true   -> trừ phiên hiện tại
   */
  async revokeAllSessions(keepCurrent = false) {
    const url = keepCurrent ? `${API_ENDPOINTS.AUTH_CUSTOMER_SESSIONS}?keep_current=true` : API_ENDPOINTS.AUTH_CUSTOMER_SESSIONS;
    return this.client.delete(url);
  }
  /**
   * Get current user profile
   */
  async getCurrentUser() {
    return this.client.get(API_ENDPOINTS.USERS_ME);
  }
  /**
   * Check if user is authenticated (has valid tokens)
   */
  async isAuthenticated() {
    return this.client.isAuthenticated();
  }
};
function createAuthService(client) {
  return new AuthService(client);
}

// src/services/users.service.ts
var UsersService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List users with pagination and filters
   */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.USERS,
      params
    );
  }
  /**
   * Get user by ID
   */
  async getById(id) {
    return this.client.get(`${API_ENDPOINTS.USERS}${id}/`);
  }
  /**
   * Get current user profile
   */
  async getMe() {
    return this.client.get(API_ENDPOINTS.USERS_ME);
  }
  /**
   * Create a new user
   * Requires AI_ADMIN role
   */
  async create(data) {
    return this.client.post(
      API_ENDPOINTS.USERS,
      data
    );
  }
  /**
   * Update a user
   */
  async update(id, data) {
    return this.client.put(
      `${API_ENDPOINTS.USERS}${id}/`,
      data
    );
  }
  /**
   * Partial update a user
   */
  async patch(id, data) {
    return this.client.patch(
      `${API_ENDPOINTS.USERS}${id}/`,
      data
    );
  }
  /**
   * Delete a user
   * Requires AI_ADMIN role
   */
  async delete(id) {
    await this.client.delete(`${API_ENDPOINTS.USERS}${id}/`);
  }
  /**
   * Activate a user
   */
  async activate(id) {
    return this.patch(id, { is_active: true });
  }
  /**
   * Deactivate a user
   */
  async deactivate(id) {
    return this.patch(id, { is_active: false });
  }
  /**
   * Đặt lại mật khẩu nhân viên — BE document 03/09/2026 (sheet 2 #3/#63).
   *
   * Body rỗng `{}` = server tự sinh mật khẩu và trả về **ĐÚNG MỘT LẦN** trong
   * `password`; truyền `newPassword` nếu muốn tự đặt. Cả hai đường đều bật cờ
   * `must_change_password` để bắt đổi ở lần đăng nhập đầu.
   *
   * ⚠️ ĐƯỜNG DẪN LÀ `reset-password` (GẠCH NGANG). Docstring cũ của BE ghi
   * `reset_password` — làm theo doc đó sẽ ăn 404.
   *
   * ⚠️ Mật khẩu chỉ trả một lần: caller PHẢI hiển thị ngay cho người dùng chép
   * lại. Không có đường xem lại, chỉ có reset tiếp.
   */
  async resetPassword(id, newPassword) {
    return this.client.post(
      `${API_ENDPOINTS.USERS}${id}/reset-password/`,
      newPassword ? { new_password: newPassword } : {}
    );
  }
  /**
   * Tắt 2FA của một nhân viên khác — chỉ AI_ADMIN (sheet 2 mục #3).
   *
   * Đây là đường DUY NHẤT khi nhân viên mất máy có app authenticator: BE cố ý
   * KHÔNG phát recovery code. Nghĩa là mất máy mà không có AI_ADMIN thì tài
   * khoản đó khoá vĩnh viễn — nói rõ trong giao diện trước khi ai bật 2FA.
   */
  async disableTwoFactorFor(id) {
    return this.client.post(
      `${API_ENDPOINTS.USERS}${id}/disable-2fa/`,
      {}
    );
  }
};
function createUsersService(client) {
  return new UsersService(client);
}

// src/services/stores.service.ts
var StoresService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List stores with pagination and filters
   */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.STORES,
      params
    );
  }
  /**
   * Get store by ID
   */
  async getById(id) {
    return this.client.get(`${API_ENDPOINTS.STORES}${id}`);
  }
  /**
   * Create a new store
   * Requires AI_ADMIN role
   *
   * Payload đi THẲNG xuống axios, service KHÔNG lọc/whitelist field nào —
   * `cleanParams()` của `ApiClient` chỉ đụng query string của GET, không đụng
   * body. Nên object lồng `alcohol_curfew` và cờ `consumption_on_premise` (BE
   * 26/08/2026 đã cho `StoreCreateSerializer` nhận) qua được nguyên vẹn; chỉ có
   * key mang giá trị `undefined` là bị `JSON.stringify` bỏ, còn `null` vẫn gửi
   * đúng như FE khai — đúng thứ ta cần vì `null` mang ngữ nghĩa "xoá".
   *
   * ĐỪNG đưa `alcohol_curfew_config` vào payload: field đó read-only, BE bỏ qua
   * im lặng (xem `CreateStoreRequest`).
   */
  async create(data) {
    return this.client.post(
      API_ENDPOINTS.STORES,
      data
    );
  }
  /**
   * Update a store
   */
  async update(id, data) {
    return this.client.put(
      `${API_ENDPOINTS.STORES}${id}`,
      data
    );
  }
  /**
   * Partial update a store
   */
  async patch(id, data) {
    return this.client.patch(
      `${API_ENDPOINTS.STORES}${id}`,
      data
    );
  }
  /**
   * Delete a store
   * Requires AI_ADMIN role
   */
  async delete(id) {
    await this.client.delete(`${API_ENDPOINTS.STORES}${id}`);
  }
  /**
   * Get favorite stores
   * BE returns: [{ id, store, store_name, created_at }]
   */
  async getFavorites() {
    return this.client.get(API_ENDPOINTS.APP_FAVORITES_STORES);
  }
  /**
   * Add a store to favorites
   */
  async addFavorite(storeId) {
    await this.client.post(API_ENDPOINTS.APP_FAVORITES_STORES, { store_id: storeId });
  }
  /**
   * Remove a store from favorites (BE expects store_id as query param)
   */
  async removeFavorite(storeId) {
    await this.client.delete(`${API_ENDPOINTS.APP_FAVORITES_STORES}?store_id=${storeId}`);
  }
  /**
   * Activate a store
   *
   * Ghi qua `PATCH /stores/{id}/` nên key phải là `business_status` — model Store
   * không có cột `status` (BE 26/08/2026). Chỉ endpoint
   * `POST /stores/{id}/set-status/` mới dùng key `status` trên dây.
   */
  async activate(id) {
    return this.patch(id, { business_status: "ACTIVE" });
  }
  /**
   * Deactivate a store
   */
  async deactivate(id) {
    return this.patch(id, { business_status: "INACTIVE" });
  }
  /**
   * Close a store permanently
   */
  async close(id) {
    return this.patch(id, { business_status: "MAINTENANCE" });
  }
  /**
   * Get store stats (total, active, warehouses, capacity_utilization)
   */
  async getStats() {
    return this.client.get(
      `${API_ENDPOINTS.STORES}stats`
    );
  }
};
function createStoresService(client) {
  return new StoresService(client);
}

// src/services/categories.service.ts
var CategoriesService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List categories with pagination and filters
   */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.CATEGORIES,
      params
    );
  }
  /**
   * List root categories (parent = null)
   */
  async listRootCategories() {
    return this.list({ parent: "null" });
  }
  /**
   * Get aggregate category stats (total / active / root / leaf) over the
   * whole taxonomy. Computed server-side, ignores pagination & filters.
   */
  async getStats() {
    return this.client.get(`${API_ENDPOINTS.CATEGORIES}stats/`);
  }
  /**
   * Get category by ID
   */
  async getById(id) {
    return this.client.get(`${API_ENDPOINTS.CATEGORIES}${id}/`);
  }
  /**
   * Get child categories of a parent
   */
  async getChildren(parentId) {
    return this.client.get(
      `${API_ENDPOINTS.CATEGORIES}${parentId}/children/`
    );
  }
  /**
   * Get products in a category
   */
  async getProducts(categoryId, params) {
    return this.client.get(
      `${API_ENDPOINTS.CATEGORIES}${categoryId}/products/`,
      params
    );
  }
  /**
   * Create a new category
   */
  async create(data) {
    return this.client.post(
      API_ENDPOINTS.CATEGORIES,
      data
    );
  }
  /**
   * Sắp xếp / đổi cha hàng loạt — BE document 03/09/2026.
   * POST /categories/bulk-update/ với danh sách theo THỨ TỰ MỚI.
   *
   * Chạy trong MỘT transaction (BE vá 03/09) nên không còn cảnh lỗi giữa
   * chừng để lại thứ tự dở dang. `parent_id` tuỳ chọn — bỏ qua thì giữ cha.
   *
   * Bốn mã lỗi 400 caller PHẢI phân biệt (trước đây BE nuốt im lặng và vẫn
   * báo thành công, client gửi danh sách cũ tưởng đã lưu):
   *   categories_not_found        + errors.missing_ids
   *   parent_categories_not_found + errors.missing_parent_ids
   *   category_cycle              (kéo danh mục vào chính con cháu nó)
   *   duplicate_category_ids
   *
   * LƯU Ý phạm vi thứ tự: `sort_order` KHÔNG ràng buộc theo cha, và
   * `GET /categories/` phẳng trộn lẫn mọi cấp khi sắp xếp. Muốn thứ tự đúng
   * theo từng nhánh phải đọc `GET /categories/tree/` (hoặc `?tree=true`).
   */
  async bulkUpdate(items) {
    return this.client.post(
      `${API_ENDPOINTS.CATEGORIES}bulk-update/`,
      items
    );
  }
  /**
   * Update a category
   */
  async update(id, data) {
    return this.client.put(
      `${API_ENDPOINTS.CATEGORIES}${id}/`,
      data
    );
  }
  /**
   * Partial update a category
   */
  async patch(id, data) {
    return this.client.patch(
      `${API_ENDPOINTS.CATEGORIES}${id}/`,
      data
    );
  }
  /**
   * Delete a category
   * Requires AI_ADMIN role
   */
  async delete(id) {
    await this.client.delete(`${API_ENDPOINTS.CATEGORIES}${id}/`);
  }
  /**
   * Activate a category
   */
  async activate(id) {
    return this.patch(id, { is_active: true });
  }
  /**
   * Deactivate a category
   */
  async deactivate(id) {
    return this.patch(id, { is_active: false });
  }
  /**
   * Build category tree from flat list
   */
  buildTree(categories) {
    const categoryMap = /* @__PURE__ */ new Map();
    const rootCategories = [];
    categories.forEach((cat) => {
      categoryMap.set(cat.id, { ...cat, children: [] });
    });
    categories.forEach((cat) => {
      const categoryWithChildren = categoryMap.get(cat.id);
      if (cat.parent_id) {
        const parent = categoryMap.get(cat.parent_id);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(categoryWithChildren);
        }
      } else {
        rootCategories.push(categoryWithChildren);
      }
    });
    return rootCategories;
  }
};
function createCategoriesService(client) {
  return new CategoriesService(client);
}

// src/services/products.service.ts
var ProductsService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List products with pagination and filters
   */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.PRODUCTS,
      params
    );
  }
  /**
   * List products for a specific store (includes store-specific prices)
   */
  async listByStore(storeId, params) {
    return this.list({ ...params, store: storeId });
  }
  /**
   * Get product by ID
   */
  async getById(id, storeId) {
    return this.client.get(
      `${API_ENDPOINTS.PRODUCTS}${id}/`,
      storeId ? { store: storeId } : void 0
    );
  }
  /**
   * Các cửa hàng đang còn bán sản phẩm này (app khách — BE 06/08/2026).
   *
   * Chỉ liệt kê cửa hàng ACTIVE và còn hàng, nhiều hàng xếp trước; hết sạch
   * hoặc sản phẩm chưa ACTIVE thì trả mảng rỗng kèm 200 (KHÔNG phải lỗi).
   *
   * Dùng endpoint này thay cho `/inventory/`: endpoint kia scope theo store
   * của nhân viên (tài khoản khách luôn rỗng — bug #75) và mang giá vốn,
   * hàng đang giữ, ngưỡng đặt lại hàng.
   */
  async getAvailability(productId) {
    const response = await this.client.get(`${API_ENDPOINTS.PRODUCTS}${productId}/availability/`);
    if (Array.isArray(response)) return response;
    return response?.data ?? [];
  }
  /**
   * Get product by barcode (for POS scanning)
   */
  async getByBarcode(barcode, storeId) {
    return this.list({
      barcode,
      store: storeId,
      page_size: 1
    });
  }
  /**
   * Get product by SKU
   */
  async getBySku(sku, storeId) {
    return this.list({
      sku,
      store: storeId,
      page_size: 1
    });
  }
  /**
   * Search products by name, SKU, or barcode
   */
  async search(query, storeId, params) {
    return this.list({
      ...params,
      search: query,
      store: storeId
    });
  }
  /**
   * Create a new product
   */
  async create(data) {
    return this.client.post(
      API_ENDPOINTS.PRODUCTS,
      data
    );
  }
  /**
   * Update a product
   */
  async update(id, data) {
    return this.client.put(
      `${API_ENDPOINTS.PRODUCTS}${id}/`,
      data
    );
  }
  /**
   * Partial update a product
   */
  async patch(id, data) {
    return this.client.patch(
      `${API_ENDPOINTS.PRODUCTS}${id}/`,
      data
    );
  }
  /**
   * Delete a product
   * Requires AI_ADMIN role
   */
  async delete(id) {
    await this.client.delete(`${API_ENDPOINTS.PRODUCTS}${id}/`);
  }
  /**
   * Tạo sản phẩm HÀNG LOẠT — POST /products/bulk/ (sheet 2 mục #13/#21).
   *
   * Tối đa 100 dòng JSON mỗi lượt. KHÔNG phải đường import file: CSV/XLSX và
   * bulk update giá hiện chỉ có management command chạy CLI phía BE.
   *
   * Tên field body là `products`, KHÔNG phải `items` — BE đọc thẳng
   * `ProductBulkCreateSerializer.products`. Bản trước gửi `{ items }` nên mọi
   * lời gọi đều 400 "products: This field is required"; endpoint này chưa từng
   * chạy được lần nào. (Xác nhận từ BE 09/09/2026 khi đọc `serializers.py`.)
   *
   * Mỗi phần tử đi qua đúng `ProductCreateSerializer` của endpoint tạo đơn,
   * nên `CreateProductRequest` dùng chung là chính xác — kể cả `image_urls`.
   *
   * CẢ LÔ TRONG MỘT TRANSACTION: một dòng hỏng là toàn bộ hỏng, trả 400. Không
   * có kiểu "được 8 hỏng 2", nên không có `summary`/`errors` để đếm; thành công
   * là 201 kèm MẢNG sản phẩm vừa tạo (`ProductListSerializer`, có `image_urls`
   * để đối chiếu ảnh đã gắn chưa).
   */
  async bulkCreate(products) {
    return this.client.post(
      `${API_ENDPOINTS.PRODUCTS}bulk/`,
      { products }
    );
  }
  /**
   * Gán một danh mục cho nhiều sản phẩm cùng lúc.
   *
   * ⚠️ ENDPOINT CHƯA TỒN TẠI — đã gửi yêu cầu cho BE 19/09/2026
   * (`docs/BE_REQUEST_bulk_update_products.md`). Gọi trước khi BE ship sẽ trả
   * 404; phía UI bắt riêng mã đó và hiện "tính năng chưa lên", giống cách
   * `InventoryLossStatsPage` xử lý `/inventory/loss-stats/` lúc chờ deploy.
   *
   * KHÁC `/products/bulk/` ở chính sách lỗi: bulk-create chạy cả lô trong một
   * transaction (một dòng hỏng là 400, không tạo gì), còn ở đây mỗi dòng độc
   * lập và kết quả trả về `summary` + `failed[]`. Xem `BulkUpdateProductsResponse`.
   */
  async bulkUpdate(data) {
    return this.client.post(
      `${API_ENDPOINTS.PRODUCTS}bulk-update/`,
      data
    );
  }
  /**
   * Bản dịch của sản phẩm (sheet 2 mục #15).
   *
   * KHÔNG cần dùng để hiển thị: `GET /products/` đã tự resolve theo header
   * `Accept-Language`, và tìm kiếm cũng quét trong bản dịch. Endpoint này chỉ
   * dành cho màn NHẬP bản dịch ở admin — đừng gọi nó để "ghép tên đa ngôn ngữ"
   * ở phía hiển thị, đó là việc server đã làm.
   */
  async getTranslations(productId) {
    return this.client.get(
      `${API_ENDPOINTS.PRODUCTS}${productId}/translations/`
    );
  }
  /**
   * Ghi đè toàn bộ bản dịch của một sản phẩm.
   *
   * Body là MẢNG trần, không bọc trong object. Đây là UPSERT theo
   * `language_code` — gửi thiếu một ngôn ngữ đã có nghĩa là gì thì BE chưa nói
   * rõ, nên FE luôn gửi đủ mọi ngôn ngữ đang quản lý để không phụ thuộc vào
   * cách hiểu đó.
   */
  async upsertTranslations(productId, translations) {
    return this.client.put(
      `${API_ENDPOINTS.PRODUCTS}${productId}/translations/`,
      translations
    );
  }
  /**
   * Get favorite products
   * BE returns: [{ id, product, product_name, created_at }]
   */
  async getFavorites() {
    return this.client.get(API_ENDPOINTS.APP_FAVORITES_PRODUCTS);
  }
  /**
   * Add a product to favorites
   */
  async addFavorite(productId) {
    await this.client.post(API_ENDPOINTS.APP_FAVORITES_PRODUCTS, { product_id: productId });
  }
  /**
   * Remove a product from favorites (BE expects product_id as query param)
   */
  async removeFavorite(productId) {
    await this.client.delete(`${API_ENDPOINTS.APP_FAVORITES_PRODUCTS}?product_id=${productId}`);
  }
  /**
   * Get store-specific prices for a product
   */
  async getStorePrices(productId) {
    return this.client.get(
      `${API_ENDPOINTS.PRODUCTS}${productId}/store-prices/`
    );
  }
  /**
   * Set store-specific price for a product
   */
  async setStorePrice(productId, data) {
    return this.client.post(
      `${API_ENDPOINTS.PRODUCTS}${productId}/store-prices/`,
      data
    );
  }
  /**
   * Activate a product
   */
  async activate(id) {
    return this.patch(id, { status: "ACTIVE" });
  }
  /**
   * Deactivate a product
   */
  async deactivate(id) {
    return this.patch(id, { status: "INACTIVE" });
  }
  /**
   * Discontinue a product (sets status to INACTIVE)
   */
  async discontinue(id) {
    return this.patch(id, { status: "INACTIVE" });
  }
  /**
   * Get product stats (total, active, inactive, low_stock, total_value)
   */
  async getStats() {
    return this.client.get(
      `${API_ENDPOINTS.PRODUCTS}stats/`
    );
  }
};
function createProductsService(client) {
  return new ProductsService(client);
}

// src/services/inventory.service.ts
var InventoryService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List inventory stocks with pagination and filters
   */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.INVENTORY,
      params
    );
  }
  /**
   * List sellable products for inventory-aware sales flows
   *
   * Params đi THẲNG xuống query string: service này không whitelist, và
   * `ApiClient.get()` chỉ bỏ những key có giá trị `undefined` (chuỗi rỗng VẪN
   * được gửi). Hệ quả cho `stock_status`:
   *   - muốn "không lọc" thì phải để `undefined`, tuyệt đối không gán `''`;
   *   - giá trị lạ bị BE trả 400 chứ không trả rỗng, nên phía gọi phải chuẩn
   *     hoá trước (store-admin: `toStockStatusParam()` trong utils/stockStatus).
   *
   * Endpoint tự phân trang: `page` + `page_size` (alias `limit`, BE chặn trần
   * 100), trả `count`/`next`/`previous`/`page`/`total_pages`.
   */
  async listSellableProducts(params) {
    return this.client.get(
      API_ENDPOINTS.INVENTORY_SELLABLE_PRODUCTS,
      params
    );
  }
  /**
   * List inventory for a specific store
   */
  async listByStore(storeId, params) {
    return this.list({ ...params, store: storeId });
  }
  /**
   * Get inventory stock by ID
   */
  async getById(id) {
    return this.client.get(`${API_ENDPOINTS.INVENTORY}${id}/`);
  }
  /**
   * Get inventory for a specific product in a store
   */
  async getByProduct(productId, storeId) {
    return this.list({ product: productId, store: storeId });
  }
  /**
   * Điều chỉnh tồn MỘT dòng.
   *
   * Gửi lên `type` + `quantity` (không âm), nhận về `adjustment_type` +
   * `quantity_change` (có dấu). Hai bộ tên khác nhau cho cùng khái niệm là cố
   * ý của BE: request đi qua serializer dùng chung với `/bulk-adjust/`, còn
   * response là model đọc. Đừng "thống nhất" hai kiểu này về một.
   */
  async adjust(data) {
    return this.client.post(
      API_ENDPOINTS.INVENTORY_ADJUST,
      data
    );
  }
  /**
   * Điều chỉnh tồn HÀNG LOẠT (tối đa 100 dòng).
   *
   * BA điểm phải nhớ, đều là bẫy đã có thật:
   *
   * 1. `idempotency_key` là PER-ROW, nằm trong từng phần tử `adjustments`,
   *    KHÔNG phải header của request. Gửi lại lô dở thì dòng đã ăn quay về
   *    trong `successful` với CÙNG `adjustment_id` lần đầu và kho không bị trừ
   *    lần hai. Đừng để key rỗng — guard chống trùng của BE từng cho lọt và
   *    trừ kho HAI LẦN (đã vá 03/09/2026, nhưng đừng dựa vào bản vá đó).
   * 2. Response LUÔN HTTP 200 kể cả khi mọi dòng đều hỏng. Phải đọc
   *    `summary.failed`; `try/catch` quanh lời gọi này KHÔNG bắt được lỗi
   *    nghiệp vụ. Riêng body sai schema thì vẫn 400 bình thường.
   * 3. `quantity_change` là số nguyên — hàng cân ký chưa đi được đường này.
   *
   * Tên field body là `adjustments`, không phải `items`.
   */
  async bulkAdjust(adjustments) {
    return this.client.post(
      API_ENDPOINTS.INVENTORY_BULK_ADJUST,
      { adjustments }
    );
  }
  /**
   * Đối soát lệch tồn: các cặp (sản phẩm, cửa hàng) mà số bán và số trừ kho
   * không khớp trong `days` ngày gần nhất.
   *
   * Trả rỗng khi job nền chưa chạy — xem ghi chú vận hành ở
   * `InventoryDriftItem`.
   */
  async listDrift(params) {
    return this.client.get(
      API_ENDPOINTS.INVENTORY_DRIFT,
      params
    );
  }
  /**
   * Thống kê hao hụt theo nguyên nhân — mục #220, BE lên QA đợt deploy kế
   * 09/09/2026 (cùng đợt với field `loss_reason` trên adjust/bulk-adjust).
   *
   * `by_reason` LUÔN có đủ 4 phần tử, zero-fill — xem ghi chú ở
   * `LossStatsResponse`.
   */
  async getLossStats(params) {
    return this.client.get(
      API_ENDPOINTS.INVENTORY_LOSS_STATS,
      params
    );
  }
  // ============================================
  // Lô & hạn sử dụng — /inventory/batches/ (sheet 2 #32)
  // ============================================
  /**
   * Ghi một lô hàng.
   *
   * `apply_to_stock` là quyết định NGHIỆP VỤ, không phải cờ kỹ thuật, và mặc
   * định của BE là `false`:
   *   - `false` — CHỈ ghi sổ lô, tồn kho không đổi. Dùng khi hàng đã được cộng
   *     kho bằng đường khác (phiếu nhập), giờ chỉ bổ sung thông tin lô/hạn.
   *   - `true`  — ghi lô VÀ cộng kho một lần (BE sinh adjustment `RESTOCK`,
   *     reason `BATCH_RECEIPT`).
   *
   * Chọn sai là cộng kho HAI LẦN hoặc KHÔNG LẦN NÀO, nên phía gọi phải hỏi
   * người dùng tường minh chứ đừng để rơi vào mặc định.
   *
   * Trùng bộ ba (product, store, batch_code) → 400.
   */
  async createBatch(data) {
    return this.client.post(
      API_ENDPOINTS.INVENTORY_BATCHES,
      data
    );
  }
  /**
   * Danh sách lô.
   *
   * BE mặc định ẨN lô đã hết (`remaining_quantity` = 0); muốn thấy đủ lịch sử
   * lô thì truyền `include_empty=true`.
   *
   * Tổng `remaining_quantity` của các lô CÓ THỂ khác tồn kho — sổ lô là lớp phủ
   * chứ không phải nguồn sự thật về số lượng. Chênh lệch đó là thông tin, không
   * phải lỗi (xem ghi chú ở `InventoryBatch`).
   */
  async listBatches(params) {
    return this.client.get(
      API_ENDPOINTS.INVENTORY_BATCHES,
      params
    );
  }
  /**
   * Lô cận hạn: còn hàng, có hạn, và hạn <= hôm nay + `days`.
   *
   * Lô ĐÃ QUÁ HẠN luôn nằm trong kết quả bất kể `days` là bao nhiêu (`is_expired`
   * = true, `days_to_expiry` âm) — nên đừng lọc lại theo `days` ở client, sẽ
   * đánh rơi đúng những lô nguy hiểm nhất. Kết quả đã sắp theo hạn tăng dần.
   */
  async listExpiringBatches(params) {
    return this.client.get(
      API_ENDPOINTS.INVENTORY_BATCHES_EXPIRING,
      params
    );
  }
  // ============================================
  // Kiểm kê định kỳ — /inventory/stocktakes/ (sheet 2 #30)
  // ============================================
  /**
   * Mở phiên kiểm kê.
   *
   * Mỗi cửa hàng chỉ được có ĐÚNG MỘT phiên `OPEN` — ràng buộc ở tầng database,
   * nên mở phiên thứ hai trả 400 chứ không trả lại phiên đang mở. Phía gọi phải
   * hỏi `listStocktakes({ status: 'OPEN' })` trước và mở thẳng phiên đó.
   */
  async createStocktake(data) {
    return this.client.post(
      API_ENDPOINTS.INVENTORY_STOCKTAKES,
      data
    );
  }
  async listStocktakes(params) {
    return this.client.get(
      API_ENDPOINTS.INVENTORY_STOCKTAKES,
      params
    );
  }
  /** Chi tiết phiên: kèm `lines[]` và `totals`. */
  async getStocktake(id) {
    return this.client.get(`${API_ENDPOINTS.INVENTORY_STOCKTAKES}${id}/`);
  }
  /**
   * Lưu số đếm cho MỘT mặt hàng (PUT, không phải POST).
   *
   * Ghi đè dòng cũ nếu đếm lại, và `expected_quantity` trả về là snapshot TẠI
   * LÚC LƯU — không phải lúc mở phiên. Vì thế `variance` nghĩa là "mắt thấy trừ
   * đi thứ hệ thống tin ngay lúc đó", và đừng cache lại `expected` cũ để tự
   * tính variance ở client.
   *
   * Sản phẩm chưa có dòng tồn ở cửa hàng đó → `expected_quantity` = 0, không
   * phải lỗi. Phiên đã FINALIZED/CANCELLED thì mọi lời gọi này trả 400.
   */
  async saveStocktakeLine(id, data) {
    return this.client.put(
      `${API_ENDPOINTS.INVENTORY_STOCKTAKES}${id}/lines/`,
      data
    );
  }
  /**
   * Chốt phiên: mỗi dòng lệch sinh MỘT adjustment DELTA (`INCREASE`/`DECREASE`,
   * reason `STOCKTAKE`). Cho phép tồn âm.
   *
   * IDEMPOTENT theo kiểu "chỉ ăn một lần": bấm lần hai trả **400**, không phải
   * trả lại kết quả cũ. Nên một lỗi mạng sau khi server đã ghi xong sẽ hiện ra
   * như 400 ở lần thử lại — đọc lại phiên để biết nó đã FINALIZED chưa thay vì
   * báo "chốt thất bại".
   */
  async finalizeStocktake(id) {
    return this.client.post(
      `${API_ENDPOINTS.INVENTORY_STOCKTAKES}${id}/finalize/`
    );
  }
  /** Huỷ phiên — KHÔNG ghi bút toán nào. Phiên đã đóng băng thì trả 400. */
  async cancelStocktake(id) {
    return this.client.post(`${API_ENDPOINTS.INVENTORY_STOCKTAKES}${id}/cancel/`);
  }
  /**
   * List inventory adjustments
   */
  async listAdjustments(params) {
    return this.client.get(
      API_ENDPOINTS.INVENTORY_ADJUSTMENTS,
      params
    );
  }
  /**
   * Get low stock items for a store
   */
  async getLowStock(storeId, params) {
    return this.client.get(
      API_ENDPOINTS.INVENTORY_LOW_STOCK,
      { ...storeId ? { store_id: storeId } : {}, ...params }
    );
  }
  /**
   * Get out of stock items for a store
   */
  async getOutOfStock(storeId, params) {
    return this.list({
      store: storeId,
      status: "OUT_OF_STOCK",
      ...params
    });
  }
  // Các hàm dưới đây chỉ là đường tắt đặt sẵn `type`. Tất cả gửi `quantity`
  // KHÔNG ÂM — chiều tăng/giảm do `type` quyết định phía BE, nên `Math.abs()`
  // ở đây không phải phép phòng thủ thừa mà là điều kiện hợp lệ của payload.
  //
  // ĐÃ GỠ 04/09/2026: `recordLoss`, `transferIn`, `transferOut`. Ba loại
  // `LOSS` / `TRANSFER_IN` / `TRANSFER_OUT` KHÔNG có trong ChoiceField của BE —
  // gọi là 400. Hàng mất giờ dùng `decreaseStock`. Chuyển kho đi qua PHIẾU
  // NHẬP (`/stock-imports?mode=transfer`), không phải qua đây — đừng ánh xạ
  // tạm sang `INCREASE`/`DECREASE`, gộp hai bút toán ở hai kho làm một sẽ mất
  // liên kết khi đối soát.
  /**
   * Kiểm đếm: đặt tồn về đúng số đếm được.
   *
   * `quantity` là SỐ ĐẾM THỰC TẾ, không phải mức chênh — server tự tính chênh
   * và tăng hay giảm đều được. Đây là đường DUY NHẤT diễn đạt được "đếm thiếu"
   * (`MANUAL_CORRECTION` chỉ cộng thêm).
   */
  async setStockCount(productId, storeId, countedQuantity, reason) {
    return this.adjust({
      product_id: productId,
      store_id: storeId,
      type: "SET_ABSOLUTE",
      quantity: Math.abs(countedQuantity),
      reason
    });
  }
  /** Tăng tồn khi biết rõ số lượng thêm vào. */
  async increaseStock(productId, storeId, quantity, reason) {
    return this.adjust({
      product_id: productId,
      store_id: storeId,
      type: "INCREASE",
      quantity: Math.abs(quantity),
      reason
    });
  }
  /**
   * Giảm tồn khi biết rõ số lượng mất.
   *
   * Giảm quá tồn hiện có thì BE trả 400 `InsufficientStock` — đó là chặn thật
   * ở server, đừng chỉ dựa vào kiểm tra phía client.
   */
  async decreaseStock(productId, storeId, quantity, reason) {
    return this.adjust({
      product_id: productId,
      store_id: storeId,
      type: "DECREASE",
      quantity: Math.abs(quantity),
      reason
    });
  }
  /**
   * Record damaged stock
   */
  async recordDamage(productId, storeId, quantity, reason) {
    return this.adjust({
      product_id: productId,
      store_id: storeId,
      type: "DAMAGE",
      quantity: Math.abs(quantity),
      reason
    });
  }
};
function createInventoryService(client) {
  return new InventoryService(client);
}

// src/services/stock-imports.service.ts
var StockImportsService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List stock imports with pagination and filters
   */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.STOCK_IMPORTS,
      params
    );
  }
  /**
   * List stock imports for a specific store
   */
  async listByStore(storeId, params) {
    return this.list({ ...params, store: storeId });
  }
  /**
   * List stock imports by status
   */
  async listByStatus(status, params) {
    return this.list({ ...params, status });
  }
  /**
   * Get stock import by ID
   */
  async getById(id) {
    return this.client.get(`${API_ENDPOINTS.STOCK_IMPORTS}${id}/`);
  }
  /**
   * Create a new stock import (DRAFT status)
   */
  async create(data) {
    return this.client.post(
      API_ENDPOINTS.STOCK_IMPORTS,
      data
    );
  }
  /**
   * Update a stock import (only in DRAFT status)
   */
  async update(id, data) {
    return this.client.put(
      `${API_ENDPOINTS.STOCK_IMPORTS}${id}/`,
      data
    );
  }
  /**
   * Submit stock import for approval (DRAFT → SUBMITTED)
   */
  async submit(id) {
    return this.client.post(
      `${API_ENDPOINTS.STOCK_IMPORTS}${id}/submit/`
    );
  }
  /**
   * Approve stock import (SUBMITTED → APPROVED)
   * Requires AI_ADMIN role
   */
  async approve(id) {
    return this.client.post(
      `${API_ENDPOINTS.STOCK_IMPORTS}${id}/approve/`
    );
  }
  /**
   * Reject stock import (SUBMITTED → REJECTED)
   * Requires AI_ADMIN role
   */
  async reject(id, reason) {
    return this.client.post(
      `${API_ENDPOINTS.STOCK_IMPORTS}${id}/reject/`,
      reason ? { reason } : void 0
    );
  }
  /**
   * Start receiving goods (APPROVED → RECEIVING)
   */
  async startReceiving(id) {
    return this.client.post(
      `${API_ENDPOINTS.STOCK_IMPORTS}${id}/start-receiving/`
    );
  }
  /**
   * Ghi số lượng THỰC NHẬN cho nhiều dòng của phiếu (trạng thái RECEIVING).
   *
   * Đây mới là chỗ ghi nhận số lượng — `complete/` KHÔNG làm việc đó
   * (`CompleteSerializer` chỉ có `idempotency_key`, mọi `items` gửi vào bị bỏ).
   * Bỏ qua bước này thì mọi dòng giữ `received_quantity = 0`, và `complete/` sẽ
   * không cộng gì vào tồn kho — nguyên nhân gốc của bug #158.
   *
   * Mỗi entry định danh bằng ĐÚNG MỘT trong hai: `item_id` hoặc `product_id`.
   * Toàn bộ payload chạy trong một transaction: sai một entry là 400 và không
   * ghi gì cả, nên retry an toàn (không sợ cộng đúp).
   */
  async bulkReceive(id, data) {
    return this.client.post(
      `${API_ENDPOINTS.STOCK_IMPORTS}${id}/bulk-receive/`,
      data
    );
  }
  /**
   * Complete stock import (RECEIVING → COMPLETED).
   *
   * Chỉ cộng vào tồn kho những dòng đã có `received_quantity > 0` — tức là phải
   * gọi `bulkReceive` trước. Không dòng nào nhận được gì thì BE trả 400.
   */
  async complete(id, data) {
    return this.client.post(
      `${API_ENDPOINTS.STOCK_IMPORTS}${id}/complete/`,
      data
    );
  }
  /**
   * Cancel a stock import (only DRAFT or SUBMITTED)
   */
  async cancel(id, reason) {
    return this.client.post(
      `${API_ENDPOINTS.STOCK_IMPORTS}${id}/cancel/`,
      reason ? { reason } : void 0
    );
  }
  /**
   * Get pending imports (DRAFT + SUBMITTED)
   */
  async getPending(storeId, params) {
    return this.list({
      store: storeId,
      status: "SUBMITTED",
      // Prioritize submitted for approval
      ...params
    });
  }
  /**
   * Get imports awaiting receiving (APPROVED status)
   */
  async getAwaitingReceiving(storeId, params) {
    return this.list({
      store: storeId,
      status: "APPROVED",
      ...params
    });
  }
  /**
   * Get imports currently being received (RECEIVING status)
   */
  async getInProgress(storeId, params) {
    return this.list({
      store: storeId,
      status: "RECEIVING",
      ...params
    });
  }
};
function createStockImportsService(client) {
  return new StockImportsService(client);
}

// src/services/orders.service.ts
var OrdersService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List orders with pagination and filters
   */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.ORDERS,
      params
    );
  }
  /**
   * List orders for a specific store
   */
  async listByStore(storeId, params) {
    return this.list({ ...params, store: storeId });
  }
  /**
   * List orders for a specific customer
   */
  async listByCustomer(customerId, params) {
    return this.list({ ...params, customer: customerId });
  }
  /**
   * List orders by status
   */
  async listByStatus(status, params) {
    return this.list({ ...params, status });
  }
  /**
   * Get order by ID
   */
  async getById(id) {
    return this.client.get(`${API_ENDPOINTS.ORDERS}${id}/`);
  }
  /**
   * Thử một giỏ hàng mà KHÔNG tạo đơn — POST /orders/verify/.
   *
   * BE xác nhận 04/09/2026: đây là endpoint read-only đã có sẵn, có gác scope
   * theo cửa hàng. store-admin dùng nó cho màn thử engine khuyến mãi
   * (sheet 2 mục #41) — không cần endpoint preview riêng.
   *
   * Lưu ý: nó trả về khuyến mãi ÁP ĐƯỢC cho giỏ này, tức đã tính cả điều kiện
   * ngày giờ, cửa hàng và hạng khách. Kết quả rỗng nghĩa là không có khuyến
   * mãi nào khớp, chứ không phải hệ thống hỏng.
   */
  async verifyCart(data) {
    return this.client.post(
      API_ENDPOINTS.ORDERS_VERIFY,
      data
    );
  }
  /**
   * Get order by order number
   */
  async getByOrderNumber(orderNumber) {
    return this.list({ order_number: orderNumber });
  }
  /**
   * Get order items
   */
  async getItems(orderId) {
    return this.client.get(`${API_ENDPOINTS.ORDERS}${orderId}/items/`);
  }
  /**
   * Create a new order
   * Used by: POSApp for checkout, UserApp for mobile orders
   * 
   * @param data Order creation data
   * @param options Request options (use idempotencyKey for safe retries)
   */
  async create(data, options) {
    return this.client.post(
      API_ENDPOINTS.ORDERS,
      data,
      options
    );
  }
  /**
   * Update an order (only PENDING orders)
   */
  async update(id, data) {
    return this.client.put(
      `${API_ENDPOINTS.ORDERS}${id}/`,
      data
    );
  }
  /**
   * Confirm an order (after payment)
   * PENDING → CONFIRMED
   */
  async confirm(id) {
    return this.client.post(
      `${API_ENDPOINTS.ORDERS}${id}/confirm/`
    );
  }
  /**
   * Cancel an order
   * Only PENDING orders can be cancelled
   */
  async cancel(id, reason) {
    return this.client.post(
      `${API_ENDPOINTS.ORDERS}${id}/cancel/`,
      reason ? { reason } : void 0
    );
  }
  /**
   * Mark order as processing
   * CONFIRMED → PROCESSING
   */
  async startProcessing(id) {
    return this.client.post(
      `${API_ENDPOINTS.ORDERS}${id}/process/`
    );
  }
  /**
   * Mark order as ready
   * PROCESSING → READY
   */
  async markReady(id) {
    return this.client.post(
      `${API_ENDPOINTS.ORDERS}${id}/ready/`
    );
  }
  /**
   * Complete an order
   * READY → COMPLETED
   */
  async complete(id) {
    return this.client.post(
      `${API_ENDPOINTS.ORDERS}${id}/complete/`
    );
  }
  /**
   * Get recent orders for dashboard
   */
  async getRecent(storeId, limit = 10) {
    return this.list({
      store: storeId,
      page_size: limit,
      ordering: "-created_at"
    });
  }
  /**
   * Get pending orders (awaiting payment).
   * BE không có status 'PENDING' — trạng thái chờ thanh toán là PENDING_PAYMENT.
   */
  async getPending(storeId, params) {
    return this.list({
      store: storeId,
      status: "PENDING_PAYMENT",
      ...params
    });
  }
  /**
   * Get orders for a date range
   */
  async getByDateRange(startDate, endDate, storeId, params) {
    return this.list({
      ...params,
      store: storeId,
      created_at__gte: startDate,
      created_at__lte: endDate
    });
  }
  /**
   * Get today's orders
   */
  async getToday(storeId, params) {
    const today = (/* @__PURE__ */ new Date()).toISOString().split("T")[0];
    return this.getByDateRange(
      `${today}T00:00:00Z`,
      `${today}T23:59:59Z`,
      storeId,
      params
    );
  }
};
function createOrdersService(client) {
  return new OrdersService(client);
}

// src/services/receipts.service.ts
function receiptOrderId(receipt) {
  return receipt.order_id ?? receipt.order;
}
var ReceiptsService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Sinh hoá đơn cho một đơn (idempotent — đơn đã có thì trả bản có sẵn).
   * Chỉ nhận đơn COMPLETED/PAID/REFUNDED/PARTIALLY_REFUNDED, ngoài ra 400.
   * Đơn không thuộc người gọi: 404.
   */
  async generateByOrder(orderId, force = false) {
    return this.client.post(
      `${API_ENDPOINTS.RECEIPTS}generate/${orderId}/`,
      { force }
    );
  }
  /** Hoá đơn của một đơn hàng. 404 = chưa có receipt HOẶC đơn không phải của mình. */
  async getByOrder(orderId) {
    return this.client.get(`${API_ENDPOINTS.RECEIPTS}by-order/${orderId}/`);
  }
  async reprint(orderId, printerId) {
    return this.client.post(
      `${API_ENDPOINTS.RECEIPTS}${orderId}/reprint/`,
      printerId ? { printer_id: printerId } : {}
    );
  }
};
function createReceiptsService(client) {
  return new ReceiptsService(client);
}

// src/services/transactions.service.ts
var TransactionsService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List transactions with pagination and filters
   */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.TRANSACTIONS,
      params
    );
  }
  /**
   * Chi tiết giao dịch.
   *
   * `ref` nhận cả ba dạng (BE 06/08/2026): id giao dịch, `order_id` (UUID —
   * thứ noti thanh toán gửi kèm) và mã đơn `SES-...` (deep link).
   * Sai mã hoặc không phải đơn của khách đang đăng nhập đều trả 404.
   *
   * Endpoint này CHỈ còn GET — không có PATCH/DELETE (BE khoá 06/08/2026 để
   * khách không sửa được `total_paid` trên hoá đơn của chính mình).
   */
  async getById(ref) {
    return this.client.get(`${API_ENDPOINTS.TRANSACTIONS}${ref}/`);
  }
};
function createTransactionsService(client) {
  return new TransactionsService(client);
}

// src/services/payments.service.ts
var PaymentsService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List payment transactions with pagination and filters
   */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.PAYMENTS,
      params
    );
  }
  /**
   * List payments for a specific store
   * Note: Payments are linked to orders, filter by order's store
   */
  async listByStore(_storeId, params) {
    return this.list({ ...params });
  }
  /**
   * List payments for a specific order
   */
  async listByOrder(orderId) {
    return this.list({ order: orderId });
  }
  /**
   * Get payment transaction by ID
   */
  async getById(id) {
    return this.client.get(`${API_ENDPOINTS.PAYMENTS}${id}/`);
  }
  /**
   * Bản gọn trạng thái giao dịch. `refresh: true` -> backend hỏi thẳng cổng
   * thanh toán (mỗi lần là một request sang gateway, dùng tiết chế).
   */
  async getStatus(id, options) {
    return this.client.get(
      `${API_ENDPOINTS.PAYMENTS}${id}/status/`,
      options?.refresh ? { refresh: true } : void 0
    );
  }
  /**
   * Danh sách phương thức thanh toán khả dụng của khách.
   * Thẻ đã lưu trước, ví sau (thứ tự backend đã sắp). 404 = chưa có customer profile.
   */
  async getMethods() {
    return this.client.get(`${API_ENDPOINTS.PAYMENTS}methods/`);
  }
  /**
   * Initiate a new payment transaction
   * Status will be PENDING
   * 
   * @param data Payment creation data
   * @param options Request options (use idempotencyKey for safe retries)
   */
  async create(data, options) {
    return this.client.post(
      `${API_ENDPOINTS.PAYMENTS}initiate/`,
      data,
      // KHÔNG tự sinh idempotency key ở đây nữa.
      //
      // Bản cũ luôn gắn `payment-create-{orderId}-{Date.now()}`. Vì có timestamp,
      // MỖI lần gọi là một key khác nhau — cơ chế idempotency chưa bao giờ có tác
      // dụng: bấm nút 2 lần = 2 key = 2 giao dịch. Nó còn chặn luôn cơ chế dự
      // phòng của BE ("không có key -> tái dùng bản ghi PENDING của cùng order
      // trong 15 phút"), vì lúc nào cũng thấy có key.
      //
      // Bỏ key mặc định để rơi vào đúng nhánh tái dùng PENDING của BE. Nơi gọi
      // nào có ID cho từng LẦN thử thanh toán thì tự truyền `idempotencyKey` —
      // key phải cố định qua các lần retry tự động của cùng một thao tác, và
      // phải đổi khi người dùng chủ động thử lại.
      options
    );
  }
  /**
   * Process a payment (after card swipe, QR scan, etc.)
   * PENDING → COMPLETED or FAILED
   * 
   * @param id Payment transaction ID
   * @param data Provider response data
   * @param options Request options
   */
  async process(transactionId, data, options) {
    return this.client.post(
      `${API_ENDPOINTS.PAYMENTS}${transactionId}/confirm/`,
      data,
      {
        ...options,
        // Always use idempotency key for payment processing
        idempotencyKey: options?.idempotencyKey || this.generateIdempotencyKey(transactionId, "confirm")
      }
    );
  }
  /**
   * Refund a payment (full or partial)
   * COMPLETED → REFUNDED
   */
  async refund(id, data, options) {
    return this.client.post(
      `${API_ENDPOINTS.PAYMENTS}${id}/refund/`,
      data,
      {
        ...options,
        idempotencyKey: options?.idempotencyKey || this.generateIdempotencyKey(id, "refund")
      }
    );
  }
  /**
   * Cancel a pending payment
   * PENDING → CANCELLED
   */
  async cancel(id, reason) {
    return this.client.post(
      `${API_ENDPOINTS.PAYMENTS}${id}/cancel/`,
      reason ? { reason } : void 0
    );
  }
  /**
   * Create payment and process in one step (for cash payments)
   */
  async createAndProcess(data, options) {
    const payment = await this.create(data, options);
    if (data.payment_method === "cash") {
      await this.process(
        payment.transaction_id,
        {
          provider_transaction_id: `CASH-${Date.now()}`,
          provider_response: data.provider_response || { method: "cash" }
        },
        options
      );
    }
    return payment;
  }
  /**
   * Get payments by status
   */
  async getByStatus(status, params) {
    return this.list({ ...params, status });
  }
  /**
   * Get pending payments
   */
  async getPending(_storeId, params) {
    return this.list({
      status: "PENDING",
      ...params
    });
  }
  /**
   * Get completed payments for a date range
   */
  async getCompletedByDateRange(startDate, endDate, _storeId, params) {
    return this.list({
      status: "COMPLETED",
      created_at__gte: startDate,
      created_at__lte: endDate,
      ...params
    });
  }
  /**
   * Generate idempotency key for payment operations.
   *
   * CẢNH BÁO: có `Date.now()` nên key KHÁC NHAU ở mỗi lần gọi — nó KHÔNG chống
   * được double-click hay retry, chỉ đủ để gắn nhãn một request đơn lẻ. Giữ lại
   * cho `confirm`/`refund` vì hai thao tác đó vẫn cần một mã tham chiếu duy nhất,
   * nhưng ĐỪNG coi đây là cơ chế idempotency.
   *
   * Muốn chống trùng thật thì key phải do nơi gọi sinh ra một lần cho mỗi LẦN
   * người dùng thao tác, rồi giữ nguyên qua mọi lần retry của lần đó.
   */
  generateIdempotencyKey(referenceId, action = "create") {
    const timestamp = Date.now();
    return `payment-${action}-${referenceId}-${timestamp}`;
  }
};
function createPaymentsService(client) {
  return new PaymentsService(client);
}

// src/services/payment-methods.service.ts
var PaymentMethodsService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Danh sách thẻ đã lưu, tối đa 3.
   *
   * Hiển thị theo `is_chargeable`, KHÔNG theo `status`: thẻ `ACTIVE` đã hết hạn
   * vẫn trả `is_chargeable: false`. Thẻ không dùng được thì hiện dạng disable
   * kèm lý do, đừng ẩn — ẩn đi thì khách không hiểu vì sao thiếu thẻ.
   *
   * LUÔN trả về mảng. Endpoint này dùng envelope phân trang
   * (`{data: [...], meta: {...}}`), mà `ApiClient` chuẩn hoá envelope đó thành
   * `{count, next, previous, results}` — tức là một OBJECT. Khai kiểu trả về là
   * mảng rồi đưa thẳng object đó cho UI thì mọi `.map` / `.some` đều nổ, và nổ
   * ở tầng component chứ không phải ở đây nên rất khó lần ngược.
   *
   * Bóc ở đây thay vì ở hook: shape trên dây là việc của tầng service.
   */
  async list() {
    const response = await this.client.get(API_ENDPOINTS.PAYMENT_METHODS);
    if (Array.isArray(response)) return response;
    return response?.results ?? [];
  }
  /**
   * Mở phiên nhập thẻ, trả về URL trang do backend host để mở trong WebView.
   *
   * Backend kiểm tra điều kiện NGAY ở bước này, trước khi khách nhập số thẻ:
   * - 409 `mandate_limit_reached` — đã đủ 3 thẻ
   * - 402 `zero_amount_unavailable` — cổng thanh toán chưa bật đơn 0 đồng
   *
   * Nhờ vậy khách không phải nhập xong thẻ mới biết là không lưu được.
   */
  async setup() {
    return this.client.post(
      `${API_ENDPOINTS.PAYMENT_METHODS}setup/`
    );
  }
  /**
   * KHÔNG có `confirm()`, và đừng thêm lại.
   *
   * `POST .../confirm/` vẫn tồn tại phía backend, nhưng client KHÔNG được gọi:
   * trang nhập thẻ do backend host đã tự gửi token lên server ngay khi khách
   * bấm lưu. Gọi thêm từ app là nộp lại một token đã dùng — backend trả lỗi,
   * và lỗi đó rơi đúng vào lúc thẻ vừa lưu THÀNH CÔNG.
   *
   * Sau khi khách quay về app thì chỉ đọc lại `list()`. Trạng thái thật của thẻ
   * đến từ webhook của cổng thanh toán, không đến từ redirect.
   */
  /** Đặt thẻ mặc định. Backend chỉ nhận đúng field này. */
  async setDefault(id) {
    return this.client.patch(
      `${API_ENDPOINTS.PAYMENT_METHODS}${id}/`,
      { is_default: true }
    );
  }
  /** Xoá thẻ. 409 `mandate_in_use` nếu đang gắn với lượt vào hoặc phiên chưa xong. */
  async remove(id) {
    return this.client.delete(`${API_ENDPOINTS.PAYMENT_METHODS}${id}/`);
  }
};
function createPaymentMethodsService(client) {
  return new PaymentMethodsService(client);
}

// src/services/store-access.service.ts
var StoreAccessService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Tạo lượt vào cửa hàng: giữ tạm tiền rồi cấp mã QR.
   *
   * CHỈ gọi từ hành động bấm nút của khách. TUYỆT ĐỐI không đặt trong
   * `setInterval` hay `useEffect` có dependency thay đổi — mỗi lần gọi là một
   * lần giữ tiền của khách. Muốn lấy mã QR mới thì dùng `getEntryIntent()`.
   *
   * Lỗi trả về ở `errors.reason`, năm mã cho năm màn khác nhau:
   * - 409 `no_payment_method` / `outstanding_balance` / `account_blocked`
   * - 422 `card_expired`
   * - 402 `preauth_declined`
   *
   * Đừng gộp thành "thanh toán thất bại": mỗi mã dẫn khách đi một hướng khác
   * nhau, gộp lại là bắt khách tự đoán mình phải làm gì.
   */
  async createEntryIntent(storeId) {
    return this.client.post(
      API_ENDPOINTS.ENTRY_INTENTS,
      { store_id: storeId }
    );
  }
  /**
   * Lấy mã QR mới và trạng thái hiện tại của lượt vào.
   *
   * Đây là hàm dùng cho vòng làm mới mã QR. Mã tự xoay vòng BÊN TRONG lượt vào,
   * nên gọi hàm này không phát sinh thêm lần giữ tiền nào.
   *
   * Hết `expires_at` thì backend tự nhả tiền và chuyển `EXPIRED` — app không
   * cần gọi gì, và không được tự tạo lượt mới.
   */
  async getEntryIntent(id) {
    return this.client.get(`${API_ENDPOINTS.ENTRY_INTENTS}${id}/`);
  }
  /**
   * Huỷ lượt vào và nhả khoản giữ tạm.
   *
   * CHỈ gọi khi khách chủ động bấm huỷ. KHÔNG gọi khi rời màn hình: khách đứng
   * trước cửa chuyển sang app khác một lúc rồi quay lại là chuyện bình thường,
   * huỷ lúc đó là bắt họ bấm lại và bị giữ tiền lần nữa.
   *
   * - 409 `entry_already_used` — khách đã quét mã vào cửa. Hiện "Bạn đã vào cửa
   *   hàng rồi", đừng hiện như lỗi.
   * - 200 khi lượt vào đã `EXPIRED` / `VOIDED` — idempotent, với khách thì cả
   *   hai đều nghĩa là tiền đã về.
   *
   * Backend trả "hold released". Đừng dùng từ "hoàn tiền" ở bất kỳ đâu trong
   * giao diện: không có giao dịch nào diễn ra nên sao kê của khách sẽ không có
   * dòng nào, nói hoàn tiền là tạo ra câu hỏi không ai trả lời được.
   */
  async cancelEntryIntent(id) {
    return this.client.delete(`${API_ENDPOINTS.ENTRY_INTENTS}${id}/`);
  }
};
function createStoreAccessService(client) {
  return new StoreAccessService(client);
}

// src/services/disputes.service.ts
var DisputesService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Khách tạo khiếu nại cho một phiên đã trả tiền.
   *
   * ĐỪNG tự kiểm điều kiện ở client rồi mới cho bấm. Đọc `can_dispute` trên
   * từng dòng hoá đơn — hạn khiếu nại gắn với chính sách lưu footage nên backend
   * mới là bên biết luật.
   *
   * Lỗi trả ở `errors.reason`, cùng bộ mã với `dispute_blocked_reason`:
   * - 409 `already_disputed` — dòng này đã khiếu nại rồi, mỗi dòng một lần
   * - 409 `dispute_window_closed` — quá hạn
   * - 409 `session_not_settled` — phiên chưa trừ tiền xong
   * - 400 — `quantity_claimed` lớn hơn số lượng trong đơn
   */
  async createForSession(sessionId, data) {
    return this.client.post(
      `${API_ENDPOINTS.SESSIONS}${sessionId}/disputes/`,
      data
    );
  }
  /** Khiếu nại của một phiên, phía khách. */
  async listForSession(sessionId) {
    return this.client.get(
      `${API_ENDPOINTS.SESSIONS}${sessionId}/disputes/`
    );
  }
  /** Hàng đợi xử lý — chỉ store-admin. */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.DISPUTES,
      params
    );
  }
  /**
   * Nhân viên chốt từng dòng — chỉ store-admin.
   *
   * Máy không bao giờ tự từ chối; mọi ca `REJECTED` đều đi qua đây. Vì vậy
   * `note` nên có với ca từ chối: nó là thứ duy nhất khách đọc được để hiểu vì
   * sao, backend trả lại dưới tên `resolution_note`.
   */
  async resolve(disputeId, data) {
    return this.client.post(
      `${API_ENDPOINTS.DISPUTES}${disputeId}/resolve/`,
      data
    );
  }
};
function createDisputesService(client) {
  return new DisputesService(client);
}

// src/services/sessions.service.ts
var SessionsService = class {
  constructor(client) {
    this.client = client;
  }
  /** Phiên đang mở của khách hiện tại. Trả null khi không ở trong cửa hàng. */
  async getActive() {
    return this.client.get(`${API_ENDPOINTS.SESSIONS}active/`);
  }
  async getById(id) {
    return this.client.get(`${API_ENDPOINTS.SESSIONS}${id}/`);
  }
  /**
   * Khách phê duyệt hoá đơn -> backend tạo Order và chuyển sang chờ thanh toán.
   * `expected_total` để backend đối chiếu, chống lệch giá giữa lúc xem và lúc duyệt.
   */
  async approve(data) {
    return this.client.post(
      `${API_ENDPOINTS.SESSIONS}${data.session_id}/approve/`,
      data
    );
  }
  /**
   * Ghim một khuyến mãi cho phiên, hoặc `null` để backend quay về tự chọn
   * mức giảm tốt nhất. Trả về hoá đơn phiên đã tính lại.
   *
   * LUÔN gửi key `promotion_id` — thiếu key là 400, gửi `null` mới hợp lệ.
   * Lỗi 409 kèm `errors.reason`: `promotion_not_applicable` (giỏ đổi sau khi
   * mở sheet), `empty_basket`, hoặc phiên đã duyệt/đã trả tiền.
   */
  async setPromotion(id, promotionId) {
    return this.client.post(
      `${API_ENDPOINTS.SESSIONS}${id}/promotion/`,
      { promotion_id: promotionId }
    );
  }
  /**
   * Voucher/khuyến mãi áp được cho GIỎ HIỆN TẠI (BE 06/08/2026).
   *
   * Backend lọc sẵn + sắp theo số tiền giảm giảm dần, nên client hiển thị
   * nguyên thứ tự. Dùng thay `/promotions/my-vouchers/` ở màn hoá đơn phiên.
   */
  async listPromotions(id) {
    const response = await this.client.get(`${API_ENDPOINTS.SESSIONS}${id}/promotions/`);
    if (Array.isArray(response)) return response;
    return response?.data ?? [];
  }
  /**
   * Mở lại phiên đã duyệt để khách sửa giỏ (PAYING -> OPEN).
   *
   * Cần khi khách bấm back khỏi màn thanh toán để lấy/bỏ thêm hàng: duyệt xong
   * phiên bị khoá, không nhận cập nhật từ cảm biến nữa. Chỉ hợp lệ khi CHƯA có
   * giao dịch thanh toán nào đang chạy — backend từ chối (409) nếu đã initiate.
   */
  async reopen(id) {
    return this.client.post(
      `${API_ENDPOINTS.SESSIONS}${id}/reopen/`,
      {}
    );
  }
  /** Huỷ phiên (khách không mua gì). */
  async cancel(id) {
    return this.client.post(
      `${API_ENDPOINTS.SESSIONS}${id}/cancel/`,
      {}
    );
  }
  // Exit token (`/sessions/{id}/exit-token/`) đã bị BE xoá 08/2026 — cổng ra
  // do hệ thống AI/camera xử lý, app không hiển thị QR ra cửa nữa.
};
function createSessionsService(client) {
  return new SessionsService(client);
}

// src/services/returns.service.ts
function pick(...values) {
  return values.find((v) => v !== void 0 && v !== null);
}
function normalizeReturnableOrder(raw) {
  const r = raw ?? {};
  return {
    order_id: pick(r.order_id, r.id) ?? "",
    order_number: pick(r.order_number, r.order_code) ?? "",
    receipt_number: pick(r.receipt_number, r.order_code) ?? null,
    user_id: pick(r.user_id) ?? null,
    store_id: pick(r.store_id) ?? null,
    store_name: pick(r.store_name) ?? "",
    store_city: pick(r.store_city) ?? null,
    purchased_at: pick(r.purchased_at, r.created_at) ?? "",
    item_count: Number(pick(r.item_count) ?? 0),
    total_amount: pick(r.total_amount, r.total) ?? "0",
    currency: pick(r.currency) ?? "",
    currency_symbol: pick(r.currency_symbol) ?? null,
    returnable_until: pick(r.returnable_until) ?? null
  };
}
function normalizeReturnableItem(raw, orderCurrency) {
  const r = raw ?? {};
  return {
    // OrderItemId là SỐ NGUYÊN (auto-increment), không phải UUID
    order_item_id: Number(pick(r.order_item_id, r.id) ?? 0),
    product_id: pick(r.product_id, r.product) ?? "",
    name: pick(r.name, r.product_name) ?? "",
    // Đính chính BE 04/08/2026: quantity = số ĐÃ MUA (không phải còn hoàn được)
    quantity: pick(r.quantity, r.purchased_quantity) ?? 0,
    purchased_quantity: pick(r.purchased_quantity) ?? null,
    returned_quantity: pick(r.returned_quantity) ?? null,
    remaining_quantity: pick(r.remaining_quantity) ?? null,
    unit_price: pick(r.unit_price) ?? "0",
    unit_refund: pick(r.unit_refund) ?? null,
    refund_amount: pick(r.refund_amount) ?? "0",
    is_returnable: pick(r.is_returnable) ?? null,
    held_by: Array.isArray(r.held_by) ? r.held_by : null,
    // Ưu tiên mảng mã thuần; BE cũ chưa có thì rút return_code từ held_by
    held_by_codes: Array.isArray(r.held_by_codes) ? r.held_by_codes.filter((c) => typeof c === "string") : Array.isArray(r.held_by) ? r.held_by.map((h) => h?.return_code).filter((c) => typeof c === "string") : null,
    currency: pick(r.currency) ?? orderCurrency ?? ""
  };
}
function normalizeReturnRequestItem(raw) {
  const r = raw ?? {};
  const product = r.product ?? null;
  return {
    ...r,
    name: pick(r.name, r.product_name, product?.name, r.product_sku, r.sku) ?? "",
    image_url: pick(
      r.image_url,
      r.product_image_url,
      r.product_image,
      product?.image_url
    ) ?? null
  };
}
function normalizeReturnRequest(raw) {
  const r = raw ?? {};
  const rawItems = Array.isArray(r.items) ? r.items : Array.isArray(r.order_items) ? r.order_items : [];
  return {
    ...r,
    items: rawItems.map(normalizeReturnRequestItem)
  };
}
var ReturnsService = class {
  constructor(client) {
    this.client = client;
  }
  // ---- Staff: tra cứu & hàng đợi (OPERATOR trở lên) ----
  /**
   * Tra cứu yêu cầu ở quầy bằng QR token HOẶC mã RET khách đọc.
   * `return_code` nhận cả `RET-000482` | `ret-000482` | `482`.
   */
  async staffScan(params) {
    const query = {};
    if (params.token) query.token = params.token;
    if (params.returnCode) query.return_code = params.returnCode;
    const response = await this.client.get(
      `${API_ENDPOINTS.RETURNS}staff/scan/`,
      query
    );
    const raw = response ?? {};
    const request = pick(raw.return_request, raw.return, raw.request);
    return {
      ...raw,
      actionable: raw.actionable === true,
      blocked_reason: raw.blocked_reason ?? null,
      return_request: request ? normalizeReturnRequest(request) : null
    };
  }
  /** Hàng đợi việc còn tồn (mặc định KHÔNG trả cả lịch sử). */
  async staffList(params) {
    const response = await this.client.get(
      `${API_ENDPOINTS.RETURNS}staff/`,
      params
    );
    if (Array.isArray(response)) {
      return {
        count: response.length,
        next: null,
        previous: null,
        results: response.map(normalizeReturnRequest)
      };
    }
    const page = response ?? {};
    return {
      ...page,
      results: Array.isArray(page.results) ? page.results.map(normalizeReturnRequest) : []
    };
  }
  // ---- Staff: quyết định về TIỀN (STORE_ADMIN / AI_ADMIN) ----
  async staffApprove(id) {
    const response = await this.client.post(
      `${API_ENDPOINTS.RETURNS}staff/${id}/approve/`
    );
    return normalizeReturnRequest(response);
  }
  /** `reason` BẮT BUỘC — thiếu là 400; lý do được lưu và gửi cho khách. */
  async staffReject(id, reason) {
    const response = await this.client.post(
      `${API_ENDPOINTS.RETURNS}staff/${id}/reject/`,
      { reason }
    );
    return normalizeReturnRequest(response);
  }
  /**
   * Hoàn tiền. BE tự làm HẾT hậu xử lý (đảo điểm, khôi phục tồn theo
   * received_quantity trừ lý do EXPIRED/QUALITY_ISSUE/DAMAGED_PACKAGING,
   * đổi trạng thái đơn, push khách) — FE KHÔNG gọi gì thêm.
   * Idempotent theo return id, bấm 2 lần an toàn.
   *
   * 409 `refund_needs_reconciliation` = có lần hoàn trước đang PENDING/FAILED,
   * tiền chưa về → KHÔNG cho retry, bảo người ta kiểm tra cổng thanh toán.
   */
  async staffRefund(id) {
    const response = await this.client.post(
      `${API_ENDPOINTS.RETURNS}staff/${id}/refund/`
    );
    return normalizeReturnRequest(response);
  }
  // ---- Staff: việc ở quầy (OPERATOR trở lên) ----
  /**
   * Nhận hàng vào kho. Body TUỲ CHỌN: khách mang đủ thì `{}`; chỉ liệt kê
   * dòng THIẾU. Response trả `refund_amount` ĐÃ TÍNH LẠI — hiện số mới cho
   * khách, đừng hiện số lúc tạo yêu cầu.
   * Phần khách không mang đến được trả lại quyền trả (vẫn nằm trong
   * returnable-items, khách tạo yêu cầu mới được).
   * Nhận nhiều hơn số khai → 400; nhận 0 toàn bộ → 400 (dùng reject).
   * Chỉ APPROVED mới receive được.
   */
  async staffReceive(id, received) {
    const body = received && received.length > 0 ? { received } : {};
    const response = await this.client.post(
      `${API_ENDPOINTS.RETURNS}staff/${id}/receive/`,
      body
    );
    return normalizeReturnRequest(response);
  }
  /**
   * Các hoá đơn còn trong hạn hoàn trả của khách hiện tại (bước 1).
   *
   * Chịu được cả hai hình dạng response: mảng trần (bản cũ) và object
   * `{ orders, customer_id, return_window_days }` (bản mới).
   */
  async listReturnableOrders() {
    const response = await this.client.get(
      `${API_ENDPOINTS.RETURNS}returnable-orders/`
    );
    if (Array.isArray(response)) {
      return { orders: response.map(normalizeReturnableOrder) };
    }
    const wrapped = response ?? {};
    return {
      orders: Array.isArray(wrapped.orders) ? wrapped.orders.map(normalizeReturnableOrder) : [],
      customer_id: wrapped.customer_id ?? null,
      user_id: wrapped.user_id ?? null,
      return_window_days: wrapped.return_window_days ?? null
    };
  }
  /**
   * Chi tiết các dòng hàng có thể hoàn của một hoá đơn (bước 2).
   *
   * `ref` nhận CẢ mã hoá đơn (SES-..., không phân biệt hoa thường) LẪN UUID
   * (BE 04/08/2026). Response kèm `order_id` thật để dùng cho bước tạo yêu cầu.
   * BE có thể trả mảng trần (bản cũ) hoặc object đầy đủ — chuẩn hoá cả hai.
   */
  async getReturnableItems(ref) {
    const response = await this.client.get(
      `${API_ENDPOINTS.RETURNS}returnable-orders/${ref}/items/`
    );
    if (Array.isArray(response)) {
      return {
        order_id: ref,
        items: response.map((item) => normalizeReturnableItem(item, null))
      };
    }
    const wrapped = response ?? {};
    const list = Array.isArray(wrapped.items) ? wrapped.items : Array.isArray(wrapped.order_items) ? wrapped.order_items : [];
    return {
      order_id: wrapped.order_id ?? ref,
      order_code: wrapped.order_code ?? null,
      user_id: wrapped.user_id ?? null,
      currency: wrapped.currency ?? null,
      currency_symbol: wrapped.currency_symbol ?? null,
      // currency ở cấp đơn (schema 04/08/2026) — điền xuống từng dòng
      items: list.map((item) => normalizeReturnableItem(item, wrapped.currency))
    };
  }
  async list(params) {
    const response = await this.client.get(
      API_ENDPOINTS.RETURNS,
      params
    );
    if (Array.isArray(response)) {
      return {
        count: response.length,
        next: null,
        previous: null,
        results: response.map(normalizeReturnRequest)
      };
    }
    const page = response ?? {};
    return {
      ...page,
      results: Array.isArray(page.results) ? page.results.map(normalizeReturnRequest) : []
    };
  }
  async getById(id) {
    const response = await this.client.get(`${API_ENDPOINTS.RETURNS}${id}/`);
    return normalizeReturnRequest(response);
  }
  /**
   * Tạo yêu cầu hoàn trả -> backend trả về mã RET + QR token (bước 3).
   *
   * Contract chốt 06/08/2026 (bug #82): `reason` + `reason_note` nằm TRONG
   * từng item (mỗi dòng hàng lý do riêng). `reason` cấp ngoài là "yêu cầu này
   * về việc gì" — BE vẫn bắt buộc, dòng nào bỏ trống thì kế thừa giá trị này.
   * Với `OTHER` chỉ cần giải thích ở MỘT chỗ, app ghi trong item nên KHÔNG
   * gửi `reason_note` cấp ngoài nữa.
   */
  async create(data) {
    const payload = {
      ...data,
      reason: data.reason ?? data.items[0]?.reason
    };
    const response = await this.client.post(
      API_ENDPOINTS.RETURNS,
      payload
    );
    return normalizeReturnRequest(response);
  }
  /**
   * Cấp lại mã QR mang tới quầy. Mã chỉ trả một lần lúc tạo (DB giữ bản băm),
   * khách đóng app là mất — gọi endpoint này xin lại. LƯU Ý: mỗi lần gọi, mã
   * cũ hết hiệu lực ngay — đừng gọi lặp/poll.
   */
  async reissueQr(id) {
    return this.client.post(
      `${API_ENDPOINTS.RETURNS}${id}/qr/`
    );
  }
  async cancel(id, reason) {
    const response = await this.client.post(
      `${API_ENDPOINTS.RETURNS}${id}/cancel/`,
      reason ? { reason } : {}
    );
    return normalizeReturnRequest(response);
  }
};
function createReturnsService(client) {
  return new ReturnsService(client);
}

// src/services/feedback.service.ts
import {
  FEEDBACK_RATING_VALUES,
  FeedbackStatus,
  FeedbackType,
  STAFF_FEEDBACK_MAX_PAGE_SIZE
} from "@conai/shared-types";
var FEEDBACK_STAFF = `${API_ENDPOINTS.FEEDBACK}staff/`;
var FEEDBACK_TYPE_VALUES = Object.values(FeedbackType);
var FEEDBACK_STATUS_VALUES = Object.values(FeedbackStatus);
var DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
function toEnumValue(value, allowed) {
  return allowed.includes(value) ? value : void 0;
}
function toEnumCsv(value, allowed) {
  if (value === void 0 || value === null) return void 0;
  const requested = new Set(Array.isArray(value) ? value : [value]);
  const kept = allowed.filter((candidate) => requested.has(candidate));
  return kept.length ? kept.join(",") : void 0;
}
function toRatingBound(value) {
  return FEEDBACK_RATING_VALUES.includes(value) ? value : void 0;
}
function toDateOnly(value) {
  return typeof value === "string" && DATE_ONLY_PATTERN.test(value) ? value : void 0;
}
function toPage(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return void 0;
  const page = Math.floor(value);
  return page >= 1 ? page : void 0;
}
function toPageSize(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return void 0;
  const size = Math.floor(value);
  if (size < 1) return void 0;
  return Math.min(size, STAFF_FEEDBACK_MAX_PAGE_SIZE);
}
function buildStaffFeedbackQuery(params = {}) {
  return {
    store: params.store || void 0,
    product_id: params.product_id || void 0,
    feedback_type: toEnumValue(params.feedback_type, FEEDBACK_TYPE_VALUES),
    status: toEnumCsv(params.status, FEEDBACK_STATUS_VALUES),
    rating: toEnumCsv(params.rating, FEEDBACK_RATING_VALUES),
    min_rating: toRatingBound(params.min_rating),
    max_rating: toRatingBound(params.max_rating),
    date_from: toDateOnly(params.date_from),
    date_to: toDateOnly(params.date_to),
    // Chỉ gửi khi BẬT. `open=false` không phải "lấy phần đã xử lý" — BE không
    // định nghĩa nghĩa đó, nên gửi lên là đoán mò.
    open: params.open === true ? true : void 0
  };
}
var FeedbackService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Lịch sử góp ý của chính khách + cờ "danh sách có bị cắt trang không".
   *
   * `GET /feedback/` hiện dùng `BaseDataResponse.success` nên envelope THIẾU
   * `meta` (BE tự phát hiện 22/08/2026, đã nhận sửa sang `list_success`). Mà
   * interceptor của ApiClient phân biệt shape bằng đúng khoá đó:
   *   có `meta`     -> `{ count, next, previous, results: [...] }`  (OBJECT)
   *   không `meta`  -> `data`                                        (MẢNG TRẦN)
   * Tức là ngày BE sửa xong, endpoint này im lặng đổi từ mảng sang object.
   * Khai kiểu trả về là mảng rồi đưa thẳng cho UI thì mọi `.map`/`.find` nổ ở
   * tầng component chứ không phải ở đây — rất khó lần ngược. Chốt về mảng ngay
   * tại chỗ, chịu được CẢ HAI shape, để BE deploy lúc nào cũng được.
   *
   * BE xác nhận 24/08/2026: endpoint HIỆN KHÔNG phân trang (`APIView` thuần).
   * Nhưng chính bản sửa sang `list_success` SẼ kéo theo phân trang. Lúc đó
   * `count > results.length` và cách đối chiếu client-side để đánh dấu "đã có
   * góp ý" vỡ IM LẶNG: góp ý rơi khỏi trang 1 -> FE không thấy -> không hiện
   * dấu -> khách gõ xong mới ăn 409. Vì thế phát hiện ngay tại đây và báo lên
   * bằng `truncated`, theo đúng nguyên tắc: THÀ KHÔNG CÓ DẤU CÒN HƠN DẤU SAI.
   *
   * SHAPE ĐỌC Ở ĐÂY LÀ SAU INTERCEPTOR, không phải body thô của BE. Body thô là
   * `{data: [...], meta: {count, next, previous}}`; `ApiClient` (client.ts:112-119)
   * đã đổi thành `{count, next, previous, results}` trước khi tới service. Nên
   * `response.results` / `response.count` ở dưới là đúng — BE có nêu nghi vấn
   * chỗ này ngày 24/08, đã đối chiếu và xác nhận không phải lỗi.
   */
  async listPage(params) {
    const response = await this.client.get(
      API_ENDPOINTS.FEEDBACK,
      params
    );
    if (Array.isArray(response)) return { items: response, truncated: false };
    const items = response?.results ?? [];
    const count = typeof response?.count === "number" ? response.count : items.length;
    return { items, truncated: count > items.length || !!response?.next };
  }
  /**
   * Chỉ lấy danh sách, bỏ qua cờ phân trang.
   *
   * Chỉ hợp cho chỗ CHỈ HIỂN THỊ: thiếu vài bản ghi cũ thì khách cuộn không
   * thấy — khó chịu nhưng không nói dối. Chỗ nào dùng danh sách để KẾT LUẬN
   * "chưa có góp ý" (dấu trong các sheet chọn) thì PHẢI gọi `listPage` và tôn
   * trọng `truncated`. user-app hiện đi qua `listPage` ở cả hai đường
   * (`useMyFeedback` / `useMyFeedbackTruncated`) vì hai màn dùng chung cache.
   */
  async list(params) {
    const { items } = await this.listPage(params);
    return items;
  }
  async getById(id) {
    return this.client.get(`${API_ENDPOINTS.FEEDBACK}${id}/`);
  }
  /**
   * Gửi góp ý. Ràng buộc BE:
   * - Phải có ≥1 trong: rating / comment / photo_urls, không thì 400
   * - feedback_type = PRODUCT_ISSUE thì bắt buộc product_id + issue_kind
   * - store_id optional với mọi loại
   */
  async create(data) {
    return this.client.post(
      API_ENDPOINTS.FEEDBACK,
      data
    );
  }
  /**
   * TODO(BE): XÁC NHẬN `PATCH /feedback/{id}/` CÓ THẬT KHÔNG.
   * Trong bản trả lời chính thức ngày 22/08/2026, khi BE liệt kê contract của
   * `/feedback/` họ chỉ nhắc `POST /feedback/`, `GET /feedback/`,
   * `GET /feedback/{id}/` — không hề nhắc PATCH hay DELETE, và chính BE xác
   * nhận `/feedback/` không có trong tài liệu. Hai method dưới đây có thể là FE
   * tự khai theo một trao đổi miệng. Nếu chưa có thật thì chúng trả 404/405;
   * `_FeedbackPage.tsx` bắt riêng mã đó và báo `feedback.updateUnavailable`
   * thay vì dội câu kỹ thuật vào mặt khách, nhưng khách sẽ KHÔNG sửa được góp ý
   * nào cả — mà rule chặn trùng của BE lại không cho gửi mới. Tức là hỏng cả
   * hai đường. Cần BE xác nhận/triển khai.
   *
   * Sửa góp ý CỦA CHÍNH KHÁCH — `PATCH /feedback/{id}/` (chốt 22/08/2026).
   * CHỈ gửi rating/comment/photo_urls/issue_kind — feedback_type/store/order/
   * product không nằm trong payload này, BE không cho đổi (xem
   * `UpdateFeedbackRequest`). Dùng để "sửa" một góp ý đã tồn tại thay vì tạo
   * góp ý mới khi POST báo trùng (409 duplicate_feedback).
   */
  async update(id, data) {
    return this.client.patch(
      `${API_ENDPOINTS.FEEDBACK}${id}/`,
      data
    );
  }
  /**
   * Xoá góp ý CỦA CHÍNH KHÁCH — `DELETE /feedback/{id}/`, soft-delete phía BE
   * (chốt 22/08/2026). Sau khi xoá, góp ý biến mất khỏi cả lịch sử của khách
   * lẫn hàng đợi xử lý của cửa hàng, và giải phóng chỗ để khách gửi góp ý mới
   * cho đúng lần mua/sản phẩm đó: bản đã xoá rơi khỏi CẢ `_check_for_duplicates`
   * LẪN `_check_recent_visit`, nên "xoá rồi viết lại từ đầu" là đường hợp lệ
   * cho khách muốn thay hẳn nội dung.
   *
   * BE xác nhận 24/08/2026: trả **200 KÈM BODY** `{data: null, code: 200,
   * message: "Feedback deleted"}` — KHÔNG phải 204. Khai `<null>` chứ không
   * `<void>` cho đúng thứ interceptor nhả ra (envelope.data = null); đừng viết
   * code nào giả định "204 nên không có body", nó sai với endpoint này.
   * Giá trị trả về không mang thông tin gì -> nuốt luôn, kiểu vẫn là Promise<void>.
   */
  async remove(id) {
    await this.client.delete(`${API_ENDPOINTS.FEEDBACK}${id}/`);
  }
  // ============================================
  // Hàng đợi xử lý phía CỬA HÀNG — `/feedback/staff/` (bug #208)
  // ============================================
  // Quyền `IsStoreAdmin`. Tách hẳn khỏi 5 method trên: đó là góp ý CỦA CHÍNH
  // khách đang đăng nhập, còn dưới đây là góp ý của MỌI khách trong phạm vi
  // quản lý. Đừng gọi chéo — `list()` ở trên không nhận bất kỳ filter nào của
  // staff và sẽ lặng lẽ trả về lịch sử của chính người đang đăng nhập.
  /**
   * Danh sách góp ý cho store-admin — `GET /feedback/staff/`.
   *
   * Trả về NGUYÊN shape sau interceptor (`{count, next, previous, results}`
   * hoặc mảng trần), KHÔNG tự bóc. Chủ ý: store-admin đã có quy ước riêng
   * (`utils/apiList.ts` — `extractList`/`extractCount`) để chịu cả hai shape,
   * và `count` là thứ màn hình cần để vẽ phân trang. Bóc sẵn thành mảng ở đây
   * là ném mất `count`, rồi mỗi màn lại tự đoán "còn trang nữa không".
   *
   * Mọi tham số đi qua `buildStaffFeedbackQuery`: giá trị ngoài enum bị BỎ,
   * `page_size` kẹp ở 100, `rating`/`status` được nối thành chuỗi ngăn phẩy.
   */
  async listStaff(params) {
    return this.client.get(FEEDBACK_STAFF, {
      ...buildStaffFeedbackQuery(params),
      page: toPage(params?.page),
      page_size: toPageSize(params?.page_size)
    });
  }
  /**
   * Số liệu tổng hợp — `GET /feedback/staff/summary/`.
   *
   * KHÔNG phân trang, KHÔNG `meta` -> interceptor trả thẳng object summary.
   *
   * Nhận CÙNG bộ filter với `listStaff` (kể cả `rating`) và đi qua CÙNG một
   * hàm dựng query, nên số trên thẻ và số dòng trong bảng luôn khớp. Khi gọi,
   * truyền ĐÚNG bộ filter đang áp cho danh sách — trừ `page`/`page_size`, hai
   * cái đó không có nghĩa ở đây (summary luôn tính trên toàn bộ tập đã lọc).
   *
   * Nhắc lại hai cái bẫy đã ghi trong type:
   * - `by_status`/`by_type`/`by_issue_kind` là dict THƯA, phải `?? 0`.
   * - `rating.average` là `null` khi chưa có đánh giá nào, KHÔNG phải `0`.
   */
  async getStaffSummary(params) {
    return this.client.get(
      `${FEEDBACK_STAFF}summary/`,
      buildStaffFeedbackQuery(params)
    );
  }
  /** Chi tiết một góp ý — `GET /feedback/staff/{id}/`. Cùng shape với phần tử list. */
  async getStaffById(id) {
    return this.client.get(`${FEEDBACK_STAFF}${id}/`);
  }
  /**
   * ⚠️⚠️ HAI FIELD Ở ĐÂY GỬI THÔNG BÁO CHO KHÁCH. ĐỌC HẾT TRƯỚC KHI GỌI. ⚠️⚠️
   *
   * `PATCH /feedback/staff/{id}/` không phải một thao tác ghi thầm lặng. BE làm
   * BA việc trong một request khi payload có `status`:
   *   1. đổi trạng thái bản ghi;
   *   2. tự ghi `handled_by` / `handled_at` theo user đang đăng nhập;
   *   3. với RESOLVED/DISMISSED: BẮN NOTIFICATION tới điện thoại của khách.
   *
   * Và `store_reply` — CÂU TRẢ LỜI KHÁCH ĐỌC ĐƯỢC, không phải ghi chú nội bộ —
   * có quy tắc thông báo RIÊNG, không giống `status`:
   *   - lần gửi ĐẦU  -> BẮN thông báo (nội dung là chính câu vừa viết, cắt 140
   *     ký tự, khung dịch theo ngôn ngữ của KHÁCH);
   *   - sửa lại      -> im lặng;
   *   - rút lại (`null`) -> im lặng, và BE xoá `store_replied_at`/`store_replied_by`;
   *   - rút hẳn rồi viết lại -> BẮN lần nữa (ngoại lệ có chủ ý của BE).
   *   Gọn lại: `store_replied_at == null` ngay TRƯỚC request ⇒ lần này sẽ bắn.
   *   Đừng đọc `store_replied_at` từ RESPONSE để suy điều đó — sau khi ghi thì
   *   nó luôn có giá trị, và câu trả lời sẽ luôn ra "không bắn".
   *
   * Thông báo KHÔNG hoàn tác được. Từ đó suy ra mấy điều rất cụ thể:
   *
   * - TUYỆT ĐỐI KHÔNG gọi trong vòng lặp. "Đánh dấu đã xử lý 40 dòng" = 40
   *   thông báo đẩy tới 40 người trong vài giây. Cần thao tác hàng loạt thì
   *   hỏi BE một endpoint bulk, đừng tự ghép ở FE.
   * - KHÔNG gọi từ `onSuccess` của một mutation khác, không gắn vào effect chạy
   *   theo dữ liệu, không dùng để "đồng bộ lại trạng thái cho chắc". Chỉ gọi
   *   khi có người thật vừa bấm nút thật.
   * - Sửa MỖI ghi chú nội bộ thì CHỈ gửi `staff_note`. Gửi kèm `status` bằng
   *   đúng giá trị cũ vẫn tính là một lần đổi trạng thái ở phía BE, và khách
   *   vẫn nhận thông báo cho một thay đổi không hề xảy ra. Điều đó áp cho CẢ
   *   `store_reply`: gửi lại đúng câu cũ khi `store_replied_at` đang `null`
   *   (vừa rút xong) là một thông báo mới cho khách.
   * - Gửi `store_reply` KÈM `status` trong cùng một request thì BE chỉ bắn ĐÚNG
   *   MỘT thông báo (thông báo trả lời thắng) — không cần tách thành hai PATCH.
   *
   * Body chỉ nhận `status`, `staff_note` và `store_reply` — mọi field khác bị BE
   * bỏ qua im lặng, nên hàm này dựng lại payload thay vì chuyển tiếp `data`
   * nguyên vẹn: thà không gửi còn hơn tưởng đã gửi.
   */
  async updateStaff(id, data) {
    const body = {};
    const status = toEnumValue(data.status, FEEDBACK_STATUS_VALUES);
    if (status) body.status = status;
    if ("staff_note" in data && (data.staff_note === null || typeof data.staff_note === "string")) {
      body.staff_note = data.staff_note;
    }
    if ("store_reply" in data && (data.store_reply === null || typeof data.store_reply === "string")) {
      body.store_reply = data.store_reply;
    }
    return this.client.patch(
      `${FEEDBACK_STAFF}${id}/`,
      body
    );
  }
};
function createFeedbackService(client) {
  return new FeedbackService(client);
}

// src/services/uploads.service.ts
var UploadsService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Upload một ảnh. Gọi tuần tự khi có nhiều ảnh — backend chỉ nhận
   * 1 ảnh/request.
   */
  async uploadImage(file, entityType) {
    const form = new FormData();
    if (typeof document !== "undefined" && typeof File !== "undefined") {
      const blob = await (await fetch(file.uri)).blob();
      form.append(
        "file",
        new File([blob], file.name, { type: file.type || blob.type })
      );
    } else {
      form.append("file", file);
    }
    form.append("entity_type", entityType);
    return this.client.post(
      API_ENDPOINTS.UPLOADS_IMAGES,
      form,
      {
        // Client mặc định Content-Type: application/json — phải override,
        // nếu không backend nhận JSON header với body multipart và trả 400.
        // React Native tự chèn boundary khi body là FormData.
        headers: { "Content-Type": "multipart/form-data" }
      }
    );
  }
};
function createUploadsService(client) {
  return new UploadsService(client);
}

// src/services/customers.service.ts
var CustomersService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List customers with pagination and filters
   */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.CUSTOMERS,
      params
    );
  }
  /**
   * Search customers by name, phone, or email
   */
  async search(query, params) {
    return this.list({ ...params, search: query });
  }
  /**
   * Get customer by ID
   */
  async getById(id) {
    return this.client.get(`${API_ENDPOINTS.CUSTOMERS}${id}/`);
  }
  /**
   * Get customer by phone number (for POS lookup)
   */
  async getByPhone(phone) {
    return this.list({ phone, page_size: 1 });
  }
  /**
   * Trạng thái xác minh 18+ của chính khách.
   * Luôn 200 — chưa xác minh thì trả { status: 'UNVERIFIED' }, không 404.
   *
   * `storeId` (BE 15/09/2026) quyết định hai field `required_method` và
   * `satisfies_current_store` trong response.
   *
   * BA ĐIỀU PHẢI BIẾT TRƯỚC KHI GỌI:
   *
   * 1. **Không truyền `storeId` thì `required_method` luôn là `DOCUMENT_MRZ`** —
   *    backend nới, vì chỗ chặn thật là cổng 18+ bên đó. Muốn màn trạng thái
   *    đúng cho khách ở thị trường chỉ nhận chip thì PHẢI truyền.
   * 2. **`storeId` sai hoặc không tồn tại -> 400**, không im lặng rơi về mặc
   *    định. Truyền một id không chắc chắn là đổi một màn hiện sai lấy một màn
   *    lỗi — cân nhắc bỏ hẳn tham số thay vì đoán.
   * 3. `status: 'VERIFIED'` kèm `satisfies_current_store: false` là trạng thái
   *    THẬT, không phải dữ liệu hỏng. Xem `needsReverificationForStore()`.
   */
  async getMyAgeVerification(storeId) {
    return this.client.get(
      `${API_ENDPOINTS.CUSTOMERS}me/age-verification/`,
      // Chỉ gửi khi có: `store_id=undefined` lọt vào query string là đúng ca 400
      // ở điểm 2 bên trên.
      storeId ? { store_id: storeId } : void 0
    );
  }
  /**
   * Gửi yêu cầu xác minh 18+ (DIIA / PASSPORT / ID_CARD).
   * Backend chuyển status sang PENDING; chỉ backend mới được set VERIFIED.
   */
  async submitAgeVerification(data) {
    return this.client.post(
      `${API_ENDPOINTS.CUSTOMERS}me/age-verification/`,
      data
    );
  }
  /**
   * Huỷ yêu cầu xác minh đang PENDING để gửi lại bằng phương thức khác.
   *
   * Contract BE 06/08/2026:
   * - PENDING -> 200, body là bản ghi đã reset {status: 'UNVERIFIED', ...}
   *   (cùng shape với GET — dùng luôn, không cần GET thêm).
   * - VERIFIED/REJECTED -> 409, errors.reason = 'not_cancellable'.
   * - Chưa gửi lần nào / đã huỷ rồi -> 404 (bấm huỷ 2 lần thì lần 2 là 404,
   *   KHÔNG phải lỗi — caller tự reset về UNVERIFIED).
   */
  async cancelAgeVerification() {
    return this.client.delete(
      `${API_ENDPOINTS.CUSTOMERS}me/age-verification/`
    );
  }
  // --------------------------------------------
  // Duyệt xác minh 18+ (ADMIN — STORE_ADMIN | AI_ADMIN)
  // --------------------------------------------
  /**
   * Hồ sơ xác minh của MỘT khách (admin xem chi tiết).
   * Trả status, method, submitted_at, document_urls (tối đa 2 ảnh),
   * rejection_reason.
   */
  async getCustomerAgeVerification(customerId) {
    return this.client.get(
      `${API_ENDPOINTS.CUSTOMERS}${customerId}/age-verification/`
    );
  }
  /**
   * Duyệt / từ chối hồ sơ (chốt với BE 06/08/2026):
   * - duyệt:   { status: 'VERIFIED', verified_age }
   * - từ chối: { status: 'REJECTED', rejection_reason } — lý do hiển thị lại
   *   cho khách trên app.
   */
  async reviewAgeVerification(customerId, data) {
    return this.client.post(
      `${API_ENDPOINTS.CUSTOMERS}${customerId}/age-verification/`,
      data
    );
  }
  /**
   * Danh sách hồ sơ chờ duyệt.
   *
   * Contract ĐỀ XUẤT (BE đang bổ sung endpoint — 06/08/2026):
   * GET /customers/age-verifications/?status=PENDING&search=&page=&page_size=
   * results: AgeVerificationQueueItem[] (customer_id, customer_name,
   * customer_email, customer_phone, method, status, submitted_at,
   * document_urls), sắp xếp submitted_at cũ nhất trước.
   */
  async listAgeVerifications(params) {
    return this.client.get(
      `${API_ENDPOINTS.CUSTOMERS}age-verifications/`,
      params
    );
  }
  /**
   * Get customer by email
   */
  async getByEmail(email) {
    return this.list({ email, page_size: 1 });
  }
  /**
   * Create a new customer
   */
  async create(data) {
    return this.client.post(
      API_ENDPOINTS.CUSTOMERS,
      data
    );
  }
  /**
   * Update a customer
   */
  async update(id, data) {
    return this.client.put(
      `${API_ENDPOINTS.CUSTOMERS}${id}/`,
      data
    );
  }
  /**
   * Partial update a customer
   */
  async patch(id, data) {
    return this.client.patch(
      `${API_ENDPOINTS.CUSTOMERS}${id}/`,
      data
    );
  }
  /**
   * Delete a customer (GDPR compliance)
   * Requires AI_ADMIN role or self
   */
  async delete(id) {
    await this.client.delete(`${API_ENDPOINTS.CUSTOMERS}${id}/`);
  }
  /**
   * Gắn hồ sơ khách với một tài khoản đăng nhập (sheet 2 mục #65).
   *
   * Hồ sơ khách (`Customer`) và tài khoản đăng nhập (`User`) là hai bảng khác
   * nhau: khách mua tại quầy chỉ có hồ sơ, còn tài khoản chỉ sinh ra khi họ
   * cài app. Endpoint này nối hai thứ đó lại.
   *
   * Trả **409** khi hồ sơ đã liên kết sẵn — không phải lỗi hệ thống mà là
   * trạng thái nghiệp vụ bình thường, giao diện phải nói "đã liên kết rồi" chứ
   * đừng hiện thông báo lỗi chung.
   */
  async linkUser(customerId, userId) {
    return this.client.post(
      `${API_ENDPOINTS.CUSTOMERS}${customerId}/link-user/`,
      { user_id: userId }
    );
  }
  /**
   * Gỡ liên kết tài khoản đăng nhập khỏi hồ sơ khách.
   *
   * KHÔNG có body — truyền object rỗng là đúng hợp đồng.
   */
  async unlinkUser(customerId) {
    return this.client.post(
      `${API_ENDPOINTS.CUSTOMERS}${customerId}/unlink-user/`,
      {}
    );
  }
  /**
   * Get or create customer by phone (for quick POS checkout)
   * Returns existing customer if found, or creates a new one
   */
  async getOrCreate(phone, data) {
    const existing = await this.getByPhone(phone);
    if (existing.results.length > 0) {
      return existing.results[0];
    }
    return this.create({
      phone,
      full_name: data?.full_name || "Guest",
      ...data
    });
  }
  /**
   * Get registered customers (with account)
   */
  async getRegistered(params) {
    return this.list({ ...params, is_registered: true });
  }
  /**
   * Get recent customers
   */
  async getRecent(limit = 10) {
    return this.list({
      page_size: limit
    });
  }
  /**
   * Get customer stats (total, new this month, VIP count)
   */
  async getStats() {
    return this.client.get(
      `${API_ENDPOINTS.CUSTOMERS}stats/`
    );
  }
  // ============================================
  // Data export cá nhân (GDPR/NĐ13) — BE 29/08/2026
  // ============================================
  /**
   * Yêu cầu xuất dữ liệu cá nhân.
   * POST /customers/profile/data-export/ — 201 khi tạo mới (status PENDING),
   * 200 + request cũ khi đang có request chạy dở (double-tap vô hại).
   */
  async requestDataExport() {
    return this.client.post(
      `${API_ENDPOINTS.CUSTOMERS}profile/data-export/`,
      {}
    );
  }
  /**
   * Trạng thái yêu cầu export gần nhất.
   * GET /customers/profile/data-export/ — 7 key, `download_url` chỉ khác null
   * khi READY và chưa hết hạn (link sống 48h).
   */
  async getDataExportStatus() {
    return this.client.get(
      `${API_ENDPOINTS.CUSTOMERS}profile/data-export/`
    );
  }
  /**
   * Tải file export (JSON attachment, KHÔNG bọc envelope).
   * GET /customers/profile/data-export/{id}/download/
   * Mã lỗi: 410 link hết hạn (yêu cầu lại) · 409 chưa READY · 404 không phải
   * của mình.
   */
  async downloadDataExport(id) {
    return this.client.get(
      `${API_ENDPOINTS.CUSTOMERS}profile/data-export/${id}/download/`
    );
  }
  // ============================================
  // Nợ sau khi rời cửa hàng — BE 29/08/2026
  // ============================================
  /**
   * Danh sách nợ + tổng theo tiền tệ + trạng thái chặn cửa.
   * GET /customers/profile/outstanding/
   * `totals` tách theo currency (không cộng chéo); tiền đều là string.
   */
  async getOutstandingDebts() {
    return this.client.get(
      `${API_ENDPOINTS.CUSTOMERS}profile/outstanding/`
    );
  }
  /**
   * Lấy link thanh toán cho một khoản nợ (Stripe Checkout, có Apple/Google
   * Pay). Idempotent — gọi hai lần trả cùng link; cũng là đường khách tự lấy
   * lại link khi mất SMS.
   * 409 `debt_not_outstanding` — nợ đã trả/đã xoá.
   * 503 `payment_link_unavailable` — chưa tạo được link (LƯU Ý: cho tới khi
   * ops điền STRIPE_SECRET_KEY, endpoint LUÔN trả 503 — hành vi đúng).
   */
  async payDebt(debtId) {
    return this.client.post(
      `${API_ENDPOINTS.CUSTOMERS}profile/outstanding/${debtId}/pay/`,
      {}
    );
  }
  // ============================================
  // Consent — BE 03/09/2026
  // ============================================
  /** Trạng thái hiện tại + history của mọi consent. */
  async getConsents() {
    return this.client.get(
      `${API_ENDPOINTS.CUSTOMERS}profile/consents/`
    );
  }
  /**
   * Ghi một consent. REPLAY-SAFE: gửi lại consent đang active cùng type +
   * cùng document_version → 200 + row cũ, không ghi trùng — cứ đẩy hết mốc
   * local lên, gửi nhiều lần vô hại.
   */
  async recordConsent(data) {
    return this.client.post(
      `${API_ENDPOINTS.CUSTOMERS}profile/consents/`,
      data
    );
  }
  /**
   * Rút lại consent (append-only: ghi withdrawn_at, không xoá row).
   * Type sai chính tả → 400 `unknown_consent_type` (không phải 404).
   */
  async withdrawConsent(type) {
    await this.client.delete(
      `${API_ENDPOINTS.CUSTOMERS}profile/consents/${type}/`
    );
  }
};
function createCustomersService(client) {
  return new CustomersService(client);
}

// src/services/loyalty.service.ts
var LoyaltyService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List loyalty accounts with pagination and filters
   */
  async listAccounts(params) {
    return this.client.get(
      API_ENDPOINTS.LOYALTY_ACCOUNTS,
      params
    );
  }
  /**
   * Get loyalty account by ID
   */
  async getAccountById(id) {
    return this.client.get(`${API_ENDPOINTS.LOYALTY_ACCOUNTS}${id}/`);
  }
  /**
   * Get loyalty account by customer ID
   */
  async getAccountByCustomer(customerId) {
    const accounts = await this.listAccounts({ customer: customerId });
    return accounts.results.length > 0 ? accounts.results[0] : null;
  }
  /**
   * Get loyalty accounts by tier
   */
  async getAccountsByTier(tier, params) {
    return this.listAccounts({ ...params, tier });
  }
  /**
   * Earn points for an order
   * 
   * @param data Earn points data
   * @param options Request options
   */
  async earnPoints(data, options) {
    return this.client.post(
      `${API_ENDPOINTS.LOYALTY_ACCOUNTS.replace("accounts/", "")}earn/`,
      data,
      {
        ...options,
        // Use idempotency key to prevent double points
        idempotencyKey: options?.idempotencyKey || `earn-${data.customer_id}-${data.amount}`
      }
    );
  }
  /**
   * Chỉnh điểm THỦ CÔNG — contract BE chốt 03/09/2026:
   * POST /loyalty/adjust/ { customer_id, points, reason }
   * - points âm hoặc dương, khác 0; trừ quá số dư bị BE từ chối (không âm).
   * - reason BẮT BUỘC, ghi vào audit log kèm actor.
   * - role: STORE_ADMIN, AI_ADMIN.
   * LƯU Ý LỊCH SỬ: đường `/loyalty/accounts/{id}/earn/` trong API_REFERENCE
   * bản cũ CHƯA TỪNG tồn tại trong code BE — đừng dựng lại. Còn
   * `earnPoints()` phía trên (POST /loyalty/earn/) là đường THẬT để cộng
   * điểm theo đơn/amount: BE tự tính điểm theo tier, caller không chọn số
   * điểm; role OPERATOR trở lên.
   */
  async adjustPoints(data, options) {
    return this.client.post(
      `${API_ENDPOINTS.LOYALTY_ACCOUNTS.replace("accounts/", "")}adjust/`,
      data,
      options
    );
  }
  /**
   * TẶNG điểm thưởng — POST /loyalty/bonus/ (BE chốt 09/09/2026).
   *
   * Body: `{ customer_id, points, reason }` — bắt buộc cả ba. Quyền: store
   * admin. Entry sinh ra mang `type = BONUS`.
   *
   * KHÁC `adjustPoints()` ở CHỖ NÀO và tại sao phải tách:
   *   - `/loyalty/adjust/` luôn sinh entry `ADJUST` và KHÔNG nhận `entry_type`.
   *     Muốn có `BONUS` thì chỉ đường này, không có cách nào khác.
   *   - `ADJUST` mang nghĩa "sửa sai số" (gõ nhầm, khiếu nại), `BONUS` mang
   *     nghĩa "tặng thêm" (chiến dịch, tri ân). Gộp làm một thì báo cáo không
   *     tách được chi phí khuyến mãi ra khỏi số điểm sửa lỗi.
   *
   * `points` ở đây LUÔN DƯƠNG — tặng thì không có chiều âm. Muốn trừ thì dùng
   * `adjustPoints()` với số âm.
   */
  async grantBonusPoints(data, options) {
    return this.client.post(
      `${API_ENDPOINTS.LOYALTY_ACCOUNTS.replace("accounts/", "")}bonus/`,
      data,
      options
    );
  }
  /**
   * Redeem points for discount
   *
   * @param accountId Loyalty account ID
   * @param data Redeem points data
   * @param options Request options
   */
  async redeemPoints(accountId, data, options) {
    return this.client.post(
      `${API_ENDPOINTS.LOYALTY_ACCOUNTS}${accountId}/redeem/`,
      data,
      options
    );
  }
  /**
   * Get points history for an account
   */
  async getPointsHistory(accountId, params) {
    return this.client.get(
      `${API_ENDPOINTS.LOYALTY_ACCOUNTS}${accountId}/entries/`,
      params
    );
  }
  /**
   * Get current user's loyalty account (me)
   */
  /**
   * Tài khoản loyalty của khách đang đăng nhập.
   *
   * `next_tier` do BACKEND tính và là NGUỒN DUY NHẤT cho ngưỡng lên hạng —
   * ngưỡng nay cấu hình được theo currency (xem `LoyaltyTierConfig`), nên mọi
   * hằng số ngưỡng ở client đều sai. BE không gửi field thì UI ẩn phần "còn X
   * điểm nữa", không tự bịa số.
   */
  async getMe() {
    return this.client.get(`${API_ENDPOINTS.LOYALTY_ACCOUNTS}me/`);
  }
  /**
   * Lịch sử điểm của khách đang đăng nhập.
   *
   * `GET /loyalty/history/?type=&limit=&offset=` — route chốt với BE
   * 06/08/2026. Đường cũ `/loyalty/accounts/me/entries/` KHÔNG tồn tại phía
   * backend. Phân trang theo `limit`/`offset`, không phải `page`/`page_size`.
   *
   * Mỗi dòng có `balance_after` = số dư khả dụng ngay sau giao dịch đó, ghi
   * tại thời điểm phát sinh (đúng cả khi có điều chỉnh tay / số dư kẹp ở 0).
   * Dòng cũ trước migration loyalty/0002 có thể `null` -> caller tự suy ngược.
   */
  async getMyPointsHistory(params) {
    return this.client.get(
      API_ENDPOINTS.LOYALTY_HISTORY,
      params
    );
  }
  /**
   * Calculate points to earn from order amount
   * Default: 1 point per 10,000 VND
   * 
   * @param amount Order amount in VND
   * @param tier Customer tier (for multiplier)
   */
  calculatePointsToEarn(amount, tier) {
    const basePoints = Math.floor(amount / 1e4);
    const multiplier = this.getTierMultiplier(tier);
    return Math.floor(basePoints * multiplier);
  }
  /**
   * Calculate discount value from points
   * Default: 1 point = 1,000 VND
   * 
   * @param points Number of points
   */
  calculateDiscountValue(points) {
    return points * 1e3;
  }
  /**
   * Get tier multiplier for points earning
   */
  getTierMultiplier(tier) {
    switch (tier) {
      case "PLATINUM":
        return 2;
      case "GOLD":
        return 1.5;
      case "SILVER":
        return 1.2;
      case "BRONZE":
      default:
        return 1;
    }
  }
  /**
   * Get tier benefits description
   */
  getTierBenefits(tier) {
    switch (tier) {
      case "PLATINUM":
        return {
          discountRate: 10,
          pointsMultiplier: 2,
          freeShipping: true,
          prioritySupport: true
        };
      case "GOLD":
        return {
          discountRate: 5,
          pointsMultiplier: 1.5,
          freeShipping: true,
          prioritySupport: false
        };
      case "SILVER":
        return {
          discountRate: 2,
          pointsMultiplier: 1.2,
          freeShipping: false,
          prioritySupport: false
        };
      case "BRONZE":
      default:
        return {
          discountRate: 0,
          pointsMultiplier: 1,
          freeShipping: false,
          prioritySupport: false
        };
    }
  }
  // ============================================
  // Cấu hình ngưỡng hạng (sheet 2 mục #45)
  // ============================================
  //
  // Ngưỡng hạng CHỈ có một nguồn: backend. Hàm `getPointsForNextTier()` cũ
  // hardcode 1000/5000/20000 ở client đã bị XOÁ (BE xác nhận bỏ được) — giữ lại
  // là có hai nguồn ngưỡng song song, và bản ở client thì không ai nhớ cập nhật.
  /**
   * Danh sách ngưỡng hạng theo từng tiền tệ.
   *
   * Trả về GỒM CẢ các dòng mặc định (`source: 'default'`), nên độ dài mảng không
   * cho biết có bao nhiêu currency đã được cấu hình riêng — phải đọc `source`.
   */
  async getTierConfig() {
    return this.client.get(API_ENDPOINTS.LOYALTY_TIER_CONFIG);
  }
  /**
   * Lưu ngưỡng riêng cho một tiền tệ.
   *
   * BE validate `0 < silver < gold < platinum`, sai thì 400.
   * ⚠️ Lưu xong KHÔNG đổi hạng của khách hiện hữu — phải gọi `recalculateTiers()`.
   */
  async updateTierConfig(currency, data) {
    return this.client.put(
      `${API_ENDPOINTS.LOYALTY_TIER_CONFIG}${encodeURIComponent(currency)}/`,
      data
    );
  }
  /** Xoá cấu hình riêng của một tiền tệ, quay lại bảng mặc định của BE. */
  async resetTierConfig(currency) {
    return this.client.delete(
      `${API_ENDPOINTS.LOYALTY_TIER_CONFIG}${encodeURIComponent(currency)}/`
    );
  }
  /**
   * Tính lại hạng cho TOÀN BỘ khách theo ngưỡng đang hiệu lực.
   *
   * Đây là bước duy nhất làm ngưỡng mới có hiệu lực với khách hiện hữu; không
   * gọi thì họ giữ hạng cũ vô thời hạn.
   */
  async recalculateTiers(options) {
    return this.client.post(
      API_ENDPOINTS.LOYALTY_RECALCULATE_TIERS,
      void 0,
      options
    );
  }
};
function createLoyaltyService(client) {
  return new LoyaltyService(client);
}

// src/services/access-control.service.ts
var AccessControlService = class {
  constructor(client) {
    this.client = client;
  }
  // ============================================
  // Access Tokens (QR Entry)
  // ============================================
  /**
   * Generate a new access token (QR code) for store entry
   * Used by: UserApp
   */
  async generateToken(data) {
    return this.client.post(
      API_ENDPOINTS.ACCESS_TOKENS,
      data
    );
  }
  /**
   * Validate an access token
   * Used by: Store entry devices
   */
  async validateToken(token) {
    return this.client.get(
      `${API_ENDPOINTS.ACCESS_TOKENS}${token}/`
    );
  }
  /**
   * List access tokens
   */
  async listTokens(params) {
    return this.client.get(
      API_ENDPOINTS.ACCESS_TOKENS,
      params
    );
  }
  /**
   * Get active access tokens for a customer
   */
  async getActiveTokensForCustomer(customerId) {
    return this.listTokens({
      customer: customerId,
      is_active: true
    });
  }
  /**
   * Generate a QR access code (standalone, no store context)
   * Used by: UserApp QR Access page
   */
  async generateQR() {
    return this.client.post(API_ENDPOINTS.QR_GENERATE);
  }
  /**
   * Generate QR code for a specific store
   * Shorthand for generateToken with QR_CODE method
   */
  async generateStoreEntryQR(customerId, storeId) {
    return this.generateToken({
      customer_id: customerId,
      store_id: storeId,
      access_method: "QR_CODE"
    });
  }
  // ============================================
  // Access Events
  // ============================================
  /**
   * Log an access event (entry/exit)
   * Used by: Store devices
   */
  async logEvent(data) {
    return this.client.post(
      API_ENDPOINTS.ACCESS_EVENTS,
      data
    );
  }
  /**
   * List access events
   */
  async listEvents(params) {
    return this.client.get(
      `${API_ENDPOINTS.ACCESS_EVENTS}`,
      params
    );
  }
  /**
   * Get access events for a store
   */
  async getStoreEvents(storeId, params) {
    return this.listEvents({ ...params, store: storeId });
  }
  /**
   * Get today's access events for a store
   */
  async getTodayEvents(storeId) {
    const today = (/* @__PURE__ */ new Date()).toISOString().split("T")[0];
    return this.listEvents({
      store: storeId,
      created_at__gte: `${today}T00:00:00Z`,
      created_at__lte: `${today}T23:59:59Z`
    });
  }
  // ============================================
  // Shopping Sessions
  // ============================================
  /**
   * List shopping sessions
   */
  async listSessions(params) {
    return this.client.get(
      API_ENDPOINTS.ACCESS_SESSIONS,
      params
    );
  }
  /**
   * Get shopping session by ID
   */
  async getSessionById(id) {
    return this.client.get(
      `${API_ENDPOINTS.ACCESS_SESSIONS}${id}/`
    );
  }
  /**
   * Get shopping sessions for a store
   */
  async getStoreSessions(storeId, params) {
    return this.listSessions({ ...params, store: storeId });
  }
  /**
   * Get active shopping sessions for a store
   */
  async getActiveSessions(storeId) {
    return this.listSessions({
      store: storeId,
      is_active: true
    });
  }
  /**
   * Get shopping sessions for a customer
   */
  async getCustomerSessions(customerId, params) {
    return this.listSessions({ ...params, customer: customerId });
  }
  /**
   * Get current active session for a customer
   */
  async getCurrentSession(customerId) {
    const sessions = await this.listSessions({
      customer: customerId,
      is_active: true,
      page_size: 1
    });
    return sessions.results.length > 0 ? sessions.results[0] : null;
  }
  /**
   * Get store visitor count for today
   */
  async getTodayVisitorCount(storeId) {
    const today = (/* @__PURE__ */ new Date()).toISOString().split("T")[0];
    const sessions = await this.listSessions({
      store: storeId,
      started_at__gte: `${today}T00:00:00Z`,
      started_at__lte: `${today}T23:59:59Z`,
      page_size: 1
      // We only need count
    });
    return sessions.count;
  }
  /**
   * Check if customer is currently in store
   */
  async isCustomerInStore(customerId, storeId) {
    const session = await this.getCurrentSession(customerId);
    return session !== null && session.store_id === storeId;
  }
  // ============================================
  // Stats
  // ============================================
  /**
   * Thống kê ra/vào của hôm nay, cắt mốc ngày THEO MÚI GIỜ CỦA STORE (BE tính,
   * FE chỉ hiển thị lại `timezone` + `period_*` mà BE trả về).
   *
   * `store` TUỲ CHỌN: bỏ trống = tổng mọi store user đang quản lý. Không tự
   * thêm `store` khi caller không truyền — gửi một store ngoài phạm vi sẽ bị
   * 403.
   */
  async getSessionStats(params) {
    const query = params?.store != null ? { store: params.store } : void 0;
    return this.client.get(
      `${API_ENDPOINTS.ACCESS_SESSIONS}stats/`,
      query
    );
  }
};
function createAccessControlService(client) {
  return new AccessControlService(client);
}

// src/services/atwork.service.ts
var AtWorkService = class {
  constructor(client) {
    this.client = client;
  }
  // ============================================
  // Doors
  // ============================================
  /** Danh sách cửa (mục 3.1) */
  async listDoors() {
    return this.client.get(API_ENDPOINTS.ATWORK_DOORS);
  }
  /**
   * Mở cửa tạm thời (remote provide-access) — mục 3.2.
   * `doorId` = atwork_door_id.
   *
   * Thành công 200 -> ProvideAccessResult.
   * Lỗi: 403 (không đủ quyền), 503 (tích hợp tắt), 502 (Nedap từ chối — kèm
   * `errors.errorCode`). Các lỗi này reject qua ApiClientError; client đọc
   * `originalError.response.data.errors.errorCode` để map message thân thiện.
   */
  async provideAccess(doorId, data) {
    return this.client.post(
      `${API_ENDPOINTS.ATWORK_DOORS}${doorId}/provide-access/`,
      data ?? {}
    );
  }
  /** Lịch sử event theo cửa (mục 3.3) */
  async listDoorEvents(doorId, params) {
    return this.client.get(
      `${API_ENDPOINTS.ATWORK_DOORS}${doorId}/events/`,
      params
    );
  }
  // ============================================
  // Stats & denied
  // ============================================
  /** Thống kê số lượt mở theo ngày (mục 3.4) — đã lọc theo store scope */
  async getDailyAccessStats(params) {
    return this.client.get(
      API_ENDPOINTS.ATWORK_ACCESS_STATS_DAILY,
      params
    );
  }
  /** Danh sách denied gần đây (mục 3.5) — chỉ event ACCESS_DENIED_* */
  async getRecentDenied(params) {
    return this.client.get(
      API_ENDPOINTS.ATWORK_ACCESS_DENIED_RECENT,
      params
    );
  }
  // ============================================
  // Audit log
  // ============================================
  /** Audit log mở cửa remote (mục 3.6) */
  async listDoorCommands(params) {
    return this.client.get(
      API_ENDPOINTS.ATWORK_DOOR_COMMANDS,
      params
    );
  }
  /** Audit log lọc theo một cửa cụ thể */
  async getDoorCommandsForDoor(doorId, params) {
    return this.listDoorCommands({ ...params, door_id: doorId });
  }
  // ============================================
  // Health
  // ============================================
  /** Kiểm tra kết nối tới Nedap AtWork */
  async getHealth() {
    return this.client.get(API_ENDPOINTS.ATWORK_HEALTH);
  }
};
function createAtWorkService(client) {
  return new AtWorkService(client);
}

// src/services/promotions.service.ts
var PromotionsService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List promotions with pagination and filters
   */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.PROMOTIONS,
      params
    );
  }
  /**
   * List promotions for a specific store
   */
  async listByStore(storeId, params) {
    return this.list({ ...params, store: storeId });
  }
  /**
   * List active promotions
   */
  async listActive(storeId, params) {
    return this.list({
      ...params,
      store: storeId,
      status: "ACTIVE"
    });
  }
  /**
   * Get promotion by ID
   */
  async getById(id) {
    return this.client.get(`${API_ENDPOINTS.PROMOTIONS}${id}/`);
  }
  /**
   * Get promotion by code
   */
  async getByCode(code) {
    return this.list({ code });
  }
  /**
   * Create a new promotion
   */
  async create(data) {
    return this.client.post(
      API_ENDPOINTS.PROMOTIONS,
      data
    );
  }
  /**
   * Update a promotion
   */
  async update(id, data) {
    return this.client.put(
      `${API_ENDPOINTS.PROMOTIONS}${id}/`,
      data
    );
  }
  /**
   * Partial update a promotion
   */
  async patch(id, data) {
    return this.client.patch(
      `${API_ENDPOINTS.PROMOTIONS}${id}/`,
      data
    );
  }
  /**
   * Delete a promotion
   */
  async delete(id) {
    await this.client.delete(`${API_ENDPOINTS.PROMOTIONS}${id}/`);
  }
  /**
   * `GET /promotions/active/` — endpoint `AllowAny`, trả `PromotionListSerializer`
   * (có `start_at`, `end_at`, `description`, `promotion_type`, `points_cost`,
   * `total_quantity`) và tự dựng queryset riêng nên KHÔNG dính lọc store-scope.
   *
   * Khác hẳn `list()` / `listActive()` (`GET /promotions/?status=ACTIVE`):
   * endpoint đó KHÔNG override `get_permissions`, rơi về mặc định
   * `[IsAuthenticated, IsStoreAdmin]` -> **403 với role CUSTOMER**. Đừng dùng
   * `listActive()` ở user-app.
   *
   * `storeId` để `undefined` = không lọc theo cửa hàng: `store_id` bị loại khỏi
   * query string luôn chứ KHÔNG gửi chuỗi `"undefined"`.
   *
   * ⚠️ Hôm nay BE vẫn BẮT BUỘC `store_id`, thiếu là 400. BE đã nhận mở thành
   * tuỳ chọn nhưng chưa làm — nên nhánh `undefined` chưa gọi được thật.
   */
  async listActiveForStore(storeId, params) {
    return this.client.get(
      API_ENDPOINTS.PROMOTIONS_ACTIVE,
      {
        ...params,
        // `undefined` -> `cleanParams` của ApiClient bỏ hẳn khoá này.
        store_id: storeId
      }
    );
  }
  /**
   * List promotions for current customer
   * Returns: { data: CustomerPromotionItem[], code, message }
   */
  async listCustomerPromotions(params) {
    const res = await this.client.get(
      API_ENDPOINTS.PROMOTIONS_ME,
      params
    );
    return res;
  }
  /**
   * Claim a promotion for the current user (Free claim)
   */
  async claimPromotion(promotionId) {
    return this.client.post(
      `${API_ENDPOINTS.PROMOTIONS}${promotionId}/claim/`
    );
  }
  /**
   * Redeem a promotion using loyalty points
   */
  async redeemPromotionPoints(promotionId) {
    return this.client.post(
      `${API_ENDPOINTS.PROMOTIONS}${promotionId}/redeem/`
    );
  }
  /**
   * Generate a one-time code for a claimed promotion
   */
  async generateCode(promotionId) {
    return this.client.post(
      `${API_ENDPOINTS.PROMOTIONS}${promotionId}/generate-code/`
    );
  }
  /**
   * List my vouchers (claimed promotions).
   * Chỉ trả voucher CÓ `code` — xem `MyVoucherItem`.
   */
  async listMyVouchers(params) {
    const res = await this.client.get(
      API_ENDPOINTS.PROMOTIONS_MY_VOUCHERS,
      params
    );
    return res;
  }
  /**
   * Validate a coupon code
   * Used by: POSApp during checkout
   */
  async validateCoupon(data) {
    return this.client.post(
      API_ENDPOINTS.PROMOTIONS_VALIDATE,
      data
    );
  }
  /**
   * Activate a promotion (DRAFT → ACTIVE)
   */
  async activate(id) {
    return this.client.post(
      `${API_ENDPOINTS.PROMOTIONS}${id}/activate/`
    );
  }
  /**
   * Pause a promotion (ACTIVE → PAUSED)
   */
  async pause(id) {
    return this.client.post(
      `${API_ENDPOINTS.PROMOTIONS}${id}/pause/`
    );
  }
  /**
   * Resume a paused promotion (PAUSED → ACTIVE)
   */
  async resume(id) {
    return this.client.post(
      `${API_ENDPOINTS.PROMOTIONS}${id}/activate/`
    );
  }
  /**
   * Cancel a promotion
   */
  async cancel(id) {
    return this.client.post(
      `${API_ENDPOINTS.PROMOTIONS}${id}/cancel/`
    );
  }
  /**
   * Check if a coupon code is valid for an order
   * Returns discount amount or null if invalid
   */
  async checkCoupon(code, storeId, orderAmount, items) {
    try {
      const result = await this.validateCoupon({
        code,
        store_id: storeId,
        order_amount: String(orderAmount),
        items
      });
      if (result.valid) {
        return {
          valid: true,
          discount: parseFloat(result.final_discount || "0"),
          message: result.message || "Coupon applied successfully"
        };
      }
      return {
        valid: false,
        discount: 0,
        message: result.message || "Invalid coupon"
      };
    } catch (error) {
      return {
        valid: false,
        discount: 0,
        message: "Failed to validate coupon"
      };
    }
  }
  /**
   * Get promotions expiring soon
   */
  async getExpiringSoon(days = 7, storeId) {
    const endDate = /* @__PURE__ */ new Date();
    endDate.setDate(endDate.getDate() + days);
    return this.list({
      store: storeId,
      status: "ACTIVE",
      // Field trên model là `end_at` (BE 19/08/2026) — filter phải theo tên đó.
      end_at__lte: endDate.toISOString()
    });
  }
  /**
   * Get automatic promotions (no code required)
   */
  async getAutomaticPromotions(storeId) {
    return this.list({
      store: storeId,
      status: "ACTIVE",
      promotion_type: "AUTOMATIC"
    });
  }
  /**
   * Get flash sale promotions
   */
  async getFlashSales(storeId) {
    return this.list({
      store: storeId,
      status: "ACTIVE",
      promotion_type: "FLASH_SALE"
    });
  }
  /**
   * Get loyalty-exclusive promotions
   */
  async getLoyaltyPromotions(storeId) {
    return this.list({
      store: storeId,
      status: "ACTIVE",
      promotion_type: "LOYALTY"
    });
  }
};
function createPromotionsService(client) {
  return new PromotionsService(client);
}

// src/services/reports.service.ts
var ReportsService = class {
  constructor(client) {
    this.client = client;
  }
  // ============================================
  // Sales Reports
  // ============================================
  /**
   * Get daily sales report for a store
   */
  async getDailySales(storeId, date) {
    const reportDate = date || (/* @__PURE__ */ new Date()).toISOString().split("T")[0];
    return this.client.get(
      API_ENDPOINTS.REPORTS_DAILY_SALES,
      { store_id: storeId, date: reportDate }
    );
  }
  /**
   * Get sales summary for a date range
   */
  async getSalesSummary(storeId, startDate, endDate) {
    return this.client.get(
      `${API_ENDPOINTS.REPORTS_DAILY_SALES}summary/`,
      {
        store_id: storeId,
        start_date: startDate,
        end_date: endDate
      }
    );
  }
  /**
   * Get today's sales for a store
   */
  async getTodaySales(storeId) {
    const today = (/* @__PURE__ */ new Date()).toISOString().split("T")[0];
    return this.getDailySales(storeId, today);
  }
  /**
   * Get this week's sales summary
   */
  async getWeekSales(storeId) {
    const endDate = /* @__PURE__ */ new Date();
    const startDate = /* @__PURE__ */ new Date();
    startDate.setDate(startDate.getDate() - 7);
    return this.getSalesSummary(
      storeId,
      startDate.toISOString().split("T")[0],
      endDate.toISOString().split("T")[0]
    );
  }
  /**
   * Get this month's sales summary
   */
  async getMonthSales(storeId) {
    const endDate = /* @__PURE__ */ new Date();
    const startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    return this.getSalesSummary(
      storeId,
      startDate.toISOString().split("T")[0],
      endDate.toISOString().split("T")[0]
    );
  }
  /**
   * Get sales comparison between two periods
   */
  async compareSales(storeId, period1Start, period1End, period2Start, period2End) {
    const [period1, period2] = await Promise.all([
      this.getSalesSummary(storeId, period1Start, period1End),
      this.getSalesSummary(storeId, period2Start, period2End)
    ]);
    const period1Total = parseFloat(period1.net_sales || "0");
    const period2Total = parseFloat(period2.net_sales || "0");
    const changePercent = period2Total > 0 ? (period1Total - period2Total) / period2Total * 100 : 0;
    return {
      period1,
      period2,
      changePercent
    };
  }
  /**
   * Chốt cuối ngày của MỘT cửa hàng cho MỘT ngày (sheet 2 mục #53).
   *
   * ⚠️ Khác `getDailySales()` ở ranh giới ngày: endpoint này cắt theo MÚI GIỜ
   * CỦA CỬA HÀNG, còn `/reports/daily-sales/` gom theo giờ server. Đơn phát sinh
   * sát nửa đêm có thể rơi về hai ngày khác nhau giữa hai báo cáo — đó là ĐÚNG
   * theo thiết kế, đừng ép hai bên khớp số.
   *
   * `date` để trống thì BE tự lấy HÔM QUA theo giờ cửa hàng; cố tình không tự
   * tính "hôm qua" ở client vì client không biết múi giờ của cửa hàng.
   */
  async getDailyClose(params) {
    return this.client.get(API_ENDPOINTS.REPORTS_DAILY_CLOSE, {
      store_id: params.store_id,
      date: params.date
    });
  }
  // ============================================
  // Inventory Reports
  // ============================================
  /**
   * Get inventory report for a store
   */
  async getInventoryReport(storeId, params) {
    return this.client.get(
      API_ENDPOINTS.REPORTS_INVENTORY,
      { store_id: storeId, ...params }
    );
  }
  /**
   * Get low stock report for a store
   */
  async getLowStockReport(storeId) {
    return this.client.get(
      `${API_ENDPOINTS.REPORTS_INVENTORY}/low-stock/`,
      { store_id: storeId }
    );
  }
  /**
   * Get inventory value report
   */
  async getInventoryValue(storeId) {
    return this.client.get(
      `${API_ENDPOINTS.REPORTS_INVENTORY}/value/`,
      { store_id: storeId }
    );
  }
  // ============================================
  // Orders Reports
  // ============================================
  /**
   * Get orders report for a date range
   */
  async getOrdersReport(storeId, params) {
    return this.client.get(
      API_ENDPOINTS.REPORTS_ORDERS,
      {
        store_id: storeId,
        ...params
      }
    );
  }
  /**
   * Get orders by channel breakdown
   */
  async getOrdersByChannel(storeId, startDate, endDate) {
    return this.client.get(
      `${API_ENDPOINTS.REPORTS_ORDERS}/by-channel/`,
      {
        store_id: storeId,
        start_date: startDate,
        end_date: endDate
      }
    );
  }
  /**
   * Get orders by status breakdown
   */
  async getOrdersByStatus(storeId, startDate, endDate) {
    return this.client.get(
      `${API_ENDPOINTS.REPORTS_ORDERS}/by-status/`,
      {
        store_id: storeId,
        start_date: startDate,
        end_date: endDate
      }
    );
  }
  /**
   * Get average order value
   */
  async getAverageOrderValue(storeId, startDate, endDate) {
    return this.client.get(
      `${API_ENDPOINTS.REPORTS_ORDERS}/aov/`,
      {
        store_id: storeId,
        start_date: startDate,
        end_date: endDate
      }
    );
  }
  // ============================================
  // Dashboard Data
  // ============================================
  /**
   * Get dashboard summary data for a store
   */
  async getDashboardSummary(storeId) {
    const today = (/* @__PURE__ */ new Date()).toISOString().split("T")[0];
    const [todaySales, lowStockReport] = await Promise.all([
      this.getDailySales(storeId, today),
      this.getLowStockReport(storeId)
    ]);
    return {
      todaySales,
      lowStockCount: lowStockReport.items?.length || 0,
      pendingOrders: 0,
      // Should be fetched from orders service
      activeVisitors: 0
      // Should be fetched from access control service
    };
  }
};
function createReportsService(client) {
  return new ReportsService(client);
}

// src/services/suppliers.service.ts
var SuppliersService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List suppliers with pagination and filters
   */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.SUPPLIERS,
      params
    );
  }
  /**
   * Get supplier by ID
   */
  async getById(id) {
    return this.client.get(`${API_ENDPOINTS.SUPPLIERS}${id}/`);
  }
  /**
   * Search suppliers
   */
  async search(query, params) {
    return this.list({ ...params, search: query });
  }
  /**
   * Create a new supplier
   */
  async create(data, options) {
    return this.client.post(
      API_ENDPOINTS.SUPPLIERS,
      data,
      options
    );
  }
  /**
   * Update a supplier
   */
  async update(id, data, options) {
    return this.client.put(
      `${API_ENDPOINTS.SUPPLIERS}${id}/`,
      data,
      options
    );
  }
  /**
   * Partially update a supplier
   */
  async patch(id, data, options) {
    return this.client.patch(
      `${API_ENDPOINTS.SUPPLIERS}${id}/`,
      data,
      options
    );
  }
  /**
   * Delete a supplier
   */
  async delete(id, options) {
    return this.client.delete(`${API_ENDPOINTS.SUPPLIERS}${id}/`, options);
  }
  /**
   * Activate a supplier
   */
  async activate(id, options) {
    return this.client.post(
      `${API_ENDPOINTS.SUPPLIERS}${id}/activate/`,
      {},
      options
    );
  }
  /**
   * Deactivate a supplier
   */
  async deactivate(id, options) {
    return this.client.post(
      `${API_ENDPOINTS.SUPPLIERS}${id}/deactivate/`,
      {},
      options
    );
  }
  /**
   * Get active suppliers
   */
  async getActive(params) {
    return this.list({ ...params, is_active: true });
  }
  /**
   * Get suppliers by payment method
   */
  async getByPaymentMethod(method, params) {
    return this.list({ ...params, payment_method: method });
  }
};
function createSuppliersService(client) {
  return new SuppliersService(client);
}

// src/services/settings.service.ts
var SettingsService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List all settings
   */
  async list(params) {
    const response = await this.client.get(
      API_ENDPOINTS.SETTINGS,
      params
    );
    return Array.isArray(response) ? response : response.results;
  }
  /**
   * Get all settings grouped by category
   */
  async getAll() {
    return this.client.get(`${API_ENDPOINTS.SETTINGS}all/`);
  }
  /**
   * Get a specific setting by key
   */
  async get(key) {
    return this.client.get(`${API_ENDPOINTS.SETTINGS}${key}/`);
  }
  /**
   * Update a specific setting
   */
  async update(key, data, options) {
    return this.client.patch(
      `${API_ENDPOINTS.SETTINGS}${key}/`,
      data,
      options
    );
  }
  /**
   * Update multiple settings at once
   */
  async bulkUpdate(settings, options) {
    return this.client.post(
      `${API_ENDPOINTS.SETTINGS}bulk-update/`,
      settings,
      options
    );
  }
  /**
   * Get general settings
   */
  async getGeneral() {
    return this.client.get(`${API_ENDPOINTS.SETTINGS}general/`);
  }
  /**
   * Update general settings
   */
  async updateGeneral(data, options) {
    return this.client.put(
      `${API_ENDPOINTS.SETTINGS}general/`,
      data,
      options
    );
  }
  /**
   * Get notification settings
   */
  async getNotifications() {
    return this.client.get(`${API_ENDPOINTS.SETTINGS}notifications/`);
  }
  /**
   * Update notification settings
   */
  async updateNotifications(data, options) {
    return this.client.put(
      `${API_ENDPOINTS.SETTINGS}notifications/`,
      data,
      options
    );
  }
  /**
   * Get security settings
   */
  async getSecurity() {
    return this.client.get(`${API_ENDPOINTS.SETTINGS}security/`);
  }
  /**
   * Update security settings
   */
  async updateSecurity(data, options) {
    return this.client.put(
      `${API_ENDPOINTS.SETTINGS}security/`,
      data,
      options
    );
  }
  /**
   * Get integrations
   */
  async getIntegrations() {
    return this.client.get(`${API_ENDPOINTS.SETTINGS}integrations/`);
  }
  /**
   * Update integration config
   */
  async updateIntegration(name, config, options) {
    return this.client.patch(
      `${API_ENDPOINTS.SETTINGS}integrations/${name}/`,
      { config },
      options
    );
  }
  /**
   * Connect an integration
   */
  async connectIntegration(name, options) {
    return this.client.post(
      `${API_ENDPOINTS.SETTINGS}integrations/${name}/connect/`,
      {},
      options
    );
  }
  /**
   * Disconnect an integration
   */
  async disconnectIntegration(name, options) {
    return this.client.post(
      `${API_ENDPOINTS.SETTINGS}integrations/${name}/disconnect/`,
      {},
      options
    );
  }
  /**
   * Get settings stats
   */
  async getStats() {
    return this.client.get(`${API_ENDPOINTS.SETTINGS}stats/`);
  }
  /**
   * Reset settings to defaults
   */
  async resetToDefaults(options) {
    return this.client.post(
      `${API_ENDPOINTS.SETTINGS}reset/`,
      {},
      options
    );
  }
};
function createSettingsService(client) {
  return new SettingsService(client);
}

// src/services/audit-logs.service.ts
function toDateOnly2(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
var AuditLogsService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List audit logs with pagination and filters
   */
  async list(params) {
    return this.client.get(
      API_ENDPOINTS.AUDIT_LOGS,
      params
    );
  }
  /**
   * Get audit log by ID
   */
  async getById(id) {
    return this.client.get(`${API_ENDPOINTS.AUDIT_LOGS}${id}/`);
  }
  /**
   * Search audit logs
   */
  async search(query, params) {
    return this.client.get(
      API_ENDPOINTS.AUDIT_LOGS,
      { ...params, search: query }
    );
  }
  /**
   * Get audit log statistics
   */
  async getStats() {
    return this.client.get(`${API_ENDPOINTS.AUDIT_LOGS}stats/`);
  }
  /**
   * Get logs by action type
   */
  async getByAction(action, params) {
    return this.list({ ...params, action });
  }
  /**
   * Get logs by entity type
   */
  async getByEntityType(resourceType, params) {
    return this.list({ ...params, resource_type: resourceType });
  }
  /**
   * Lọc theo NGƯỜI THỰC HIỆN. Param là `actor_user_id`, không phải `user`.
   */
  async getByUser(userId, params) {
    return this.list({ ...params, actor_user_id: userId });
  }
  /**
   * Get logs for a specific entity
   */
  async getByEntity(resourceType, resourceId, params) {
    return this.list({ ...params, resource_type: resourceType, resource_id: resourceId });
  }
  /**
   * Get today's audit logs.
   *
   * `start_date`/`end_date` là NGÀY (`YYYY-MM-DD`), không phải ISO datetime —
   * gửi cả phần giờ là 400.
   */
  async getToday(params) {
    return this.list({ ...params, start_date: toDateOnly2(/* @__PURE__ */ new Date()) });
  }
  /**
   * Get logs by date range
   */
  async getByDateRange(startDate, endDate, params) {
    return this.list({
      ...params,
      start_date: toDateOnly2(startDate),
      end_date: toDateOnly2(endDate)
    });
  }
  /**
   * Export audit logs
   */
  async export(params) {
    return this.client.get(
      `${API_ENDPOINTS.AUDIT_LOGS}export/`,
      params,
      // Trước đây chỗ này ép kiểu `as unknown as undefined` nên `responseType`
      // bị `buildRequestConfig` bỏ qua — axios vẫn parse body và hàm này trả về
      // dữ liệu đã parse chứ không phải Blob. `RequestOptions.responseType` giờ
      // là field thật, bỏ được cast.
      { responseType: "blob" }
    );
  }
  // ── Sổ bằng chứng bán hàng 18+ (BE 17/08/2026) ─────────────────────────────
  //
  // Đặt ở đây vì cùng họ "audit" và service này đã được nối sẵn vào provider,
  // dù đường dẫn là `/audit/restricted-sales/` chứ không phải `/audit-logs/`.
  // Tách service riêng chỉ vì khác một tiền tố sẽ tốn thêm 5 chỗ khai báo mà
  // không đổi gì về mặt phân quyền hay vòng đời.
  /**
   * Danh sách lần bán hàng 18+.
   *
   * Trả envelope `{data, meta}`, KHÔNG phải `{count, results}` như các list
   * khác — đừng đưa vào chỗ dùng `PaginatedResponse`.
   */
  async listRestrictedSales(params) {
    return this.client.get(
      "/audit/restricted-sales/",
      params
    );
  }
  /**
   * Tải sổ dạng CSV.
   *
   * Không truyền ngày = 90 ngày gần nhất. Khoảng quá 400 ngày backend trả 400.
   */
  async exportRestrictedSales(params) {
    return this.client.get(
      "/audit/restricted-sales/export/",
      params,
      { responseType: "blob" }
    );
  }
  /**
   * Get recent activity summary
   */
  async getRecentActivity(limit = 10) {
    const response = await this.list({ limit });
    return response.results;
  }
  /**
   * Get login/logout history for a user
   */
  async getAuthHistory(userId) {
    const [loginLogs, logoutLogs] = await Promise.all([
      this.list({ actor_user_id: userId, action: "LOGIN" }),
      this.list({ actor_user_id: userId, action: "LOGOUT" })
    ]);
    const allLogs = [...loginLogs.results, ...logoutLogs.results].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return {
      count: loginLogs.count + logoutLogs.count,
      next: null,
      previous: null,
      results: allLogs
    };
  }
};
function createAuditLogsService(client) {
  return new AuditLogsService(client);
}

// src/services/notifications.service.ts
var NotificationsService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Chỉ trả mảng — giữ nguyên chữ ký cũ cho các chỗ gọi không cần tổng số
   * (user-app). Cần `meta.count` thì dùng `listWithMeta()`.
   */
  async list(params) {
    const { items } = await this.listWithMeta(params);
    return items;
  }
  /**
   * Như `list()` nhưng giữ lại `meta.count`. Bản cũ đọc mỗi `results` rồi vứt
   * phần bao ngoài, nên không chỗ nào biết tổng số thật là bao nhiêu.
   */
  async listWithMeta(params) {
    const response = await this.client.get(
      API_ENDPOINTS.APP_NOTIFICATIONS_ME,
      params
    );
    if (Array.isArray(response)) {
      return { items: response, count: response.length };
    }
    const items = response?.results ?? [];
    const count = typeof response?.meta?.count === "number" ? response.meta.count : typeof response?.count === "number" ? response.count : items.length;
    return { items, count };
  }
  async getSettings() {
    return this.client.get("/app/notifications/settings/");
  }
  async updateSettings(settings) {
    await this.client.patch("/app/notifications/settings/", settings);
  }
  async markAsRead(notificationId) {
    await this.client.patch(`/app/notifications/${notificationId}/`, { is_read: true });
  }
  async markAsUnread(notificationId) {
    await this.client.patch(`/app/notifications/${notificationId}/`, { is_read: false });
  }
  async delete(notificationId) {
    await this.client.delete(`/app/notifications/${notificationId}/`);
  }
};
function createNotificationsService(client) {
  return new NotificationsService(client);
}

// src/services/devices.service.ts
var DevicesService = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Register a device for push notifications.
   *
   * Idempotent phía backend: gọi lại với cùng `device_id` sẽ cập nhật token chứ
   * không tạo bản ghi mới, nên cứ gọi thoải mái khi token refresh.
   */
  async register(data) {
    await this.client.post(API_ENDPOINTS.APP_DEVICES_REGISTER, data);
  }
  /**
   * Unregister a device (e.g. on logout)
   */
  async unregister(deviceId) {
    await this.client.post(API_ENDPOINTS.APP_DEVICES_UNREGISTER, {
      device_id: deviceId
    });
  }
};
function createDevicesService(client) {
  return new DevicesService(client);
}

// src/index.ts
function createConAIApi(config) {
  const client = new ApiClient(config);
  return {
    client,
    auth: new AuthService(client),
    users: new UsersService(client),
    stores: new StoresService(client),
    categories: new CategoriesService(client),
    products: new ProductsService(client),
    inventory: new InventoryService(client),
    stockImports: new StockImportsService(client),
    orders: new OrdersService(client),
    payments: new PaymentsService(client),
    receipts: new ReceiptsService(client),
    sessions: new SessionsService(client),
    returns: new ReturnsService(client),
    feedback: new FeedbackService(client),
    uploads: new UploadsService(client),
    customers: new CustomersService(client),
    loyalty: new LoyaltyService(client),
    accessControl: new AccessControlService(client),
    paymentMethods: new PaymentMethodsService(client),
    storeAccess: new StoreAccessService(client),
    disputes: new DisputesService(client),
    atWork: new AtWorkService(client),
    promotions: new PromotionsService(client),
    reports: new ReportsService(client),
    suppliers: new SuppliersService(client),
    settings: new SettingsService(client),
    auditLogs: new AuditLogsService(client),
    notifications: new NotificationsService(client),
    transactions: new TransactionsService(client),
    devices: new DevicesService(client)
  };
}
var index_default = createConAIApi;
export {
  API_ENDPOINTS,
  API_ERROR_CODES,
  AccessControlService,
  ApiClient,
  AtWorkService,
  AuditLogsService,
  AuthService,
  CONTENT_TYPES,
  CategoriesService,
  CustomersService,
  DEFAULT_RETRY_CONFIG,
  DEFAULT_TIMEOUT,
  DevicesService,
  DisputesService,
  FeedbackService,
  HEADERS,
  HTTP_STATUS,
  InventoryService,
  LocalStorageTokenStorage,
  LoyaltyService,
  MemoryTokenStorage,
  NotificationsService,
  OrdersService,
  PaymentMethodsService,
  PaymentsService,
  PersistentTokenStorage,
  ProductsService,
  PromotionsService,
  ReceiptsService,
  ReportsService,
  ReturnsService,
  SecureTokenStorage,
  SessionStorageTokenStorage,
  SessionsService,
  SettingsService,
  StockImportsService,
  StoreAccessService,
  StoresService,
  SuppliersService,
  TransactionsService,
  UploadsService,
  UsersService,
  calculateBackoffDelay,
  createAccessControlService,
  createApiClient,
  createApiClientError,
  createAtWorkService,
  createAuditLogsService,
  createAuthService,
  createCategoriesService,
  createConAIApi,
  createCustomersService,
  createDevicesService,
  createDisputesService,
  createFeedbackService,
  createInventoryService,
  createLoyaltyService,
  createNotificationsService,
  createOrdersService,
  createPaymentMethodsService,
  createPaymentsService,
  createProductsService,
  createPromotionsService,
  createReceiptsService,
  createReportsService,
  createReturnsService,
  createSessionsService,
  createSettingsService,
  createStockImportsService,
  createStoreAccessService,
  createStoresService,
  createSuppliersService,
  createTokenStorage,
  createTransactionsService,
  createUploadsService,
  createUsersService,
  index_default as default,
  getStatusErrorMessage,
  isApiClientError,
  isApiError,
  isRetryableError,
  parseAxiosError,
  receiptOrderId
};
